<template>
  <div class="commission-page">
    <div class="card card-custom gutter-b">
      <div v-if="loading" class="card-body text-center py-15">
        <span class="spinner spinner-primary spinner-lg"></span>
        <div class="text-muted mt-5">Carregando regras de comissão...</div>
      </div>

      <template v-else>
        <div class="card-body pt-5">
          <div class="plan-navigation">
            <router-link
              :to="{ name: 'vendedorLista' }"
              class="btn btn-sm btn-light-primary font-weight-bold"
            >
              <b-icon icon="arrow-left" class="mr-2" />
              Vendedores
            </router-link>
            <div class="plan-navigation-summary">
              <div class="configuration-icon">
                <b-icon icon="sliders" />
              </div>
              <div>
                <strong class="d-block text-dark"
                  >Configuração de comissões</strong
                >
                <span class="text-muted">
                  Regras únicas da empresa · {{ eventoCompetenciaLabel }}
                </span>
              </div>
            </div>
          </div>

          <b-tabs v-model="tabIndex" pills class="commission-tabs">
            <b-tab title="Visão geral">
              <div class="analysis-filter mb-5">
                <b-row align-v="end">
                  <b-col lg="3" md="6">
                    <b-form-group label="Data inicial" class="mb-0">
                      <b-form-input
                        v-model="periodo.data_inicial"
                        type="date"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col lg="3" md="6" class="mt-3 mt-md-0">
                    <b-form-group label="Data final" class="mb-0">
                      <b-form-input v-model="periodo.data_final" type="date" />
                    </b-form-group>
                  </b-col>
                  <b-col lg="3" md="6" class="mt-3 mt-lg-0">
                    <b-form-group label="Vendedor" class="mb-0">
                      <b-form-select
                        v-model="periodo.id_vendedor"
                        :options="sellerOptions"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col lg="3" md="6" class="mt-3 mt-lg-0">
                    <button
                      type="button"
                      class="btn btn-primary btn-block font-weight-bold"
                      :disabled="processing"
                      @click="simular"
                    >
                      <b-spinner v-if="processing" small class="mr-2" />
                      Atualizar análise
                    </button>
                  </b-col>
                </b-row>
                <div class="quick-periods">
                  <span>Período rápido:</span>
                  <button
                    v-for="atalho in periodShortcuts"
                    :key="atalho.value"
                    type="button"
                    class="btn btn-xs"
                    :class="
                      periodoRapidoAtivo === atalho.value
                        ? 'btn-light-primary'
                        : 'btn-light'
                    "
                    :disabled="processing"
                    @click="aplicarPeriodoRapido(atalho.value)"
                  >
                    {{ atalho.text }}
                  </button>
                </div>
              </div>

              <div v-if="resultado" class="commission-overview">
                <div class="row overview-kpis">
                  <b-col
                    v-for="card in overviewCards"
                    :key="card.titulo"
                    xl="3"
                    md="6"
                    class="mb-5"
                  >
                    <div class="overview-kpi h-100">
                      <div class="overview-kpi-icon" :class="card.classe">
                        <b-icon :icon="card.icone" />
                      </div>
                      <div>
                        <span>{{ card.titulo }}</span>
                        <strong>{{ card.valor }}</strong>
                        <small>{{ card.legenda }}</small>
                      </div>
                    </div>
                  </b-col>
                </div>

                <div class="row">
                  <b-col xl="8" class="mb-5">
                    <div class="dashboard-card h-100">
                      <div class="dashboard-card-header">
                        <div>
                          <h3>Evolução das comissões</h3>
                          <p>
                            Base comissionável e valor previsto ao longo do
                            período.
                          </p>
                        </div>
                        <div class="history-grouping">
                          <button
                            v-for="opcao in historyGroupingOptions"
                            :key="opcao.value"
                            type="button"
                            class="btn btn-xs"
                            :class="
                              agrupamentoHistorico === opcao.value
                                ? 'btn-primary'
                                : 'btn-light'
                            "
                            :disabled="processing"
                            @click="alterarAgrupamentoHistorico(opcao.value)"
                          >
                            {{ opcao.text }}
                          </button>
                        </div>
                      </div>
                      <div class="dashboard-card-body history-chart">
                        <apexchart
                          height="330"
                          :options="opcoesGraficoEvolucao"
                          :series="seriesGraficoEvolucao"
                        />
                        <div v-if="!temHistorico" class="chart-empty">
                          Nenhum movimento de comissão encontrado neste período.
                        </div>
                      </div>
                    </div>
                  </b-col>
                  <b-col xl="4" class="mb-5">
                    <div class="dashboard-card h-100">
                      <div class="dashboard-card-header">
                        <div>
                          <h3>À vista x a prazo</h3>
                          <p>Composição da base em cada semana ou mês.</p>
                        </div>
                      </div>
                      <div class="dashboard-card-body history-chart">
                        <apexchart
                          type="bar"
                          height="330"
                          :options="opcoesGraficoModalidades"
                          :series="seriesGraficoModalidades"
                        />
                        <div v-if="!temHistorico" class="chart-empty">
                          Sem valores para comparar.
                        </div>
                      </div>
                    </div>
                  </b-col>
                </div>

                <div class="row">
                  <b-col xl="8" class="mb-5">
                    <div class="dashboard-card h-100">
                      <div class="dashboard-card-header">
                        <div>
                          <h3>Desempenho da equipe</h3>
                          <p>
                            Realizado no período comparado à meta individual.
                          </p>
                        </div>
                        <span class="dashboard-chip">
                          {{ eventoCompetenciaLabel }}
                        </span>
                      </div>
                      <div class="dashboard-card-body">
                        <apexchart
                          type="bar"
                          height="300"
                          :options="opcoesGraficoDesempenho"
                          :series="seriesGraficoDesempenho"
                        />
                      </div>
                    </div>
                  </b-col>
                  <b-col xl="4" class="mb-5">
                    <div class="dashboard-card h-100">
                      <div class="dashboard-card-header">
                        <div>
                          <h3>Composição a pagar</h3>
                          <p>Comissão base e bônus da competência.</p>
                        </div>
                      </div>
                      <div class="dashboard-card-body">
                        <apexchart
                          type="donut"
                          height="295"
                          :options="opcoesGraficoComposicao"
                          :series="seriesGraficoComposicao"
                        />
                        <div v-if="!temComissaoComposicao" class="chart-empty">
                          Nenhuma comissão calculada no período.
                        </div>
                      </div>
                    </div>
                  </b-col>
                </div>

                <div class="dashboard-card">
                  <div class="dashboard-card-header">
                    <div>
                      <h3>Ranking e desempenho individual</h3>
                      <p>
                        Acompanhe participação, atingimento e valor previsto por
                        vendedor.
                      </p>
                    </div>
                    <button
                      type="button"
                      class="btn btn-light-primary font-weight-bold"
                      @click="tabIndex = 5"
                    >
                      Ir para apuração
                    </button>
                  </div>
                  <div class="table-responsive">
                    <table
                      class="table table-hover table-vertical-center mb-0 ranking-table"
                    >
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Vendedor</th>
                          <th>Progresso da meta</th>
                          <th class="text-right">Realizado</th>
                          <th class="text-right">Participação</th>
                          <th class="text-right">
                            {{
                              resultadoPorRecebimento
                                ? "Comissão disponível"
                                : "Comissão prevista"
                            }}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(vendedor, index) in rankingVendedores"
                          :key="vendedor.id_vendedor"
                        >
                          <td>
                            <span class="ranking-position">{{
                              index + 1
                            }}</span>
                          </td>
                          <td>
                            <strong>{{ vendedor.nome }}</strong>
                            <small class="d-block text-muted">
                              {{
                                vendedor.meta_individual
                                  ? "Meta " + money(vendedor.meta_individual)
                                  : "Sem meta individual"
                              }}
                            </small>
                          </td>
                          <td class="progress-column">
                            <div
                              class="d-flex justify-content-between font-size-sm mb-1"
                            >
                              <span>{{
                                percent(vendedor.atingimento_individual)
                              }}</span>
                              <span
                                :class="
                                  vendedor.atingimento_individual >= 100
                                    ? 'text-success'
                                    : 'text-muted'
                                "
                              >
                                {{
                                  vendedor.atingimento_individual >= 100
                                    ? "Meta atingida"
                                    : "Em andamento"
                                }}
                              </span>
                            </div>
                            <div class="progress progress-xs">
                              <div
                                class="progress-bar"
                                :class="
                                  vendedor.atingimento_individual >= 100
                                    ? 'bg-success'
                                    : 'bg-primary'
                                "
                                :style="{
                                  width:
                                    Math.min(
                                      Number(
                                        vendedor.atingimento_individual || 0
                                      ),
                                      100
                                    ) + '%',
                                }"
                              ></div>
                            </div>
                          </td>
                          <td class="text-right">
                            {{ money(vendedor.realizado_individual) }}
                          </td>
                          <td class="text-right">
                            {{ percent(vendedor.participacao_equipe) }}
                          </td>
                          <td
                            class="text-right font-weight-bolder text-primary"
                          >
                            {{
                              money(
                                resultadoPorRecebimento
                                  ? vendedorFinance(vendedor).valor_disponivel
                                  : vendedor.total_pagar
                              )
                            }}
                          </td>
                        </tr>
                        <tr v-if="!rankingVendedores.length">
                          <td colspan="6" class="text-center text-muted py-8">
                            Nenhum desempenho encontrado neste período.
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div v-else class="empty-overview">
                <b-icon icon="bar-chart-line" font-scale="3" />
                <h4>Pronto para analisar a equipe</h4>
                <p>Selecione o período e atualize a visão geral.</p>
              </div>
            </b-tab>

            <b-tab title="Regras globais">
              <div class="section-heading tab-intro">
                <div>
                  <span class="section-eyebrow">Configuração padrão</span>
                  <h5>Regras globais da empresa</h5>
                  <p>
                    Estas regras são herdadas por todos, exceto quando houver
                    uma configuração individual.
                  </p>
                </div>
              </div>

              <div class="monthly-policy-note">
                <b-icon icon="calendar3" />
                <div>
                  <strong>Competência mensal fixa</strong>
                  <span>
                    Metas e faixas sempre consideram as vendas do dia 1 ao
                    último dia de cada mês.
                  </span>
                </div>
              </div>

              <div class="config-panel">
                <div class="config-panel-heading">
                  <div class="config-panel-icon is-coral">
                    <b-icon icon="percent" />
                  </div>
                  <div>
                    <h6>Comissão padrão</h6>
                    <p>Percentuais e operações herdados por toda a equipe.</p>
                  </div>
                </div>
                <b-row>
                  <b-col md="3">
                    <b-form-group label="Venda à vista">
                      <b-input-group append="%">
                        <b-form-input
                          v-model.number="plano.comissao_vista"
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          :disabled="!isEditable"
                        />
                      </b-input-group>
                    </b-form-group>
                  </b-col>
                  <b-col md="3">
                    <b-form-group label="Venda a prazo">
                      <b-input-group append="%">
                        <b-form-input
                          v-model.number="plano.comissao_prazo"
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          :disabled="!isEditable"
                        />
                      </b-input-group>
                    </b-form-group>
                  </b-col>
                  <b-col md="6">
                    <label class="d-block">Operações incluídas</label>
                    <div class="source-options">
                      <b-form-checkbox
                        v-model="plano.incluir_nfe"
                        :disabled="!isEditable"
                        switch
                      >
                        NF-e
                      </b-form-checkbox>
                      <b-form-checkbox
                        v-model="plano.incluir_nfce"
                        :disabled="!isEditable"
                        switch
                      >
                        NFC-e
                      </b-form-checkbox>
                      <b-form-checkbox
                        v-model="plano.incluir_pedidos"
                        :disabled="!isEditable"
                        switch
                      >
                        Pedidos faturados
                      </b-form-checkbox>
                      <b-form-checkbox
                        v-if="hasModuloServicos"
                        v-model="plano.incluir_os"
                        :disabled="!isEditable"
                        switch
                      >
                        OS faturadas
                      </b-form-checkbox>
                      <b-form-checkbox
                        v-model="plano.incluir_contratos"
                        :disabled="!isEditable"
                        switch
                      >
                        Contratos
                      </b-form-checkbox>
                    </div>
                  </b-col>
                </b-row>

                <div class="timing-setting">
                  <div class="timing-setting-copy">
                    <span>Liberação no financeiro</span>
                    <p>
                      A meta sempre usa as vendas do mês. Esta opção define
                      apenas quando a conta a pagar pode ser gerada.
                    </p>
                  </div>
                  <div class="timing-options">
                    <button
                      type="button"
                      class="timing-option"
                      :class="{
                        'is-selected':
                          plano.evento_competencia === 'faturamento',
                      }"
                      :disabled="!isEditable"
                      @click="plano.evento_competencia = 'faturamento'"
                    >
                      <b-icon icon="receipt" />
                      <span>
                        <strong>No fechamento</strong>
                        <small>Libera toda a comissão do mês</small>
                      </span>
                    </button>
                    <button
                      type="button"
                      class="timing-option"
                      :class="{
                        'is-selected':
                          plano.evento_competencia === 'recebimento',
                      }"
                      :disabled="!isEditable"
                      @click="plano.evento_competencia = 'recebimento'"
                    >
                      <b-icon icon="cash-stack" />
                      <span>
                        <strong>Após o recebimento</strong>
                        <small>Libera conforme o Contas a Receber</small>
                      </span>
                    </button>
                  </div>
                </div>
              </div>

              <div class="config-panel">
                <div class="config-panel-heading">
                  <div class="config-panel-icon is-green">
                    <b-icon icon="bullseye" />
                  </div>
                  <div>
                    <h6>Meta da equipe</h6>
                    <p>
                      Aplicada somente a vendedores sem uma meta individual.
                    </p>
                  </div>
                </div>
                <b-row>
                  <b-col md="4">
                    <b-form-group label="Meta da equipe">
                      <b-input-group prepend="R$">
                        <money
                          v-model="plano.meta_equipe"
                          class="form-control"
                          v-bind="configMoney"
                          :disabled="!isEditable"
                        />
                      </b-input-group>
                    </b-form-group>
                  </b-col>
                  <b-col md="4">
                    <b-form-group label="Tipo do bônus">
                      <b-form-select
                        v-model="plano.bonus_equipe_tipo"
                        :disabled="!isEditable"
                        :options="bonusTypeOptions"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col md="4">
                    <b-form-group
                      :label="bonusValueLabel(plano.bonus_equipe_tipo)"
                    >
                      <b-input-group
                        v-if="isMoneyBonusType(plano.bonus_equipe_tipo)"
                        prepend="R$"
                      >
                        <money
                          v-model="plano.bonus_equipe_valor"
                          class="form-control"
                          v-bind="configMoney"
                          :disabled="!isEditable"
                        />
                      </b-input-group>
                      <b-form-input
                        v-else-if="!plano.bonus_equipe_tipo"
                        disabled
                        placeholder="Selecione o tipo"
                      />
                      <b-input-group v-else append="%">
                        <b-form-input
                          v-model.number="plano.bonus_equipe_valor"
                          type="number"
                          min="0"
                          step="0.01"
                          :disabled="!isEditable || !plano.bonus_equipe_tipo"
                        />
                      </b-input-group>
                    </b-form-group>
                  </b-col>
                </b-row>
              </div>
            </b-tab>

            <b-tab title="Metas individuais">
              <div class="section-heading">
                <div>
                  <h5>Meta e comissão por vendedor</h5>
                  <p>
                    Defina a meta e escolha uma tabela progressiva quando o
                    percentual de comissão depender do atingimento.
                  </p>
                </div>
              </div>
              <div class="inheritance-note">
                <b-icon icon="layers" />
                <span>
                  <strong>Regra simples:</strong> com uma tabela selecionada,
                  ela substitui as comissões à vista e a prazo. Sem tabela,
                  permanecem os percentuais individuais ou globais atuais.
                </span>
              </div>
              <div class="table-responsive">
                <table class="table table-head-custom table-vertical-center">
                  <thead>
                    <tr>
                      <th>Vendedor</th>
                      <th class="text-center">Comissionar</th>
                      <th>Meta individual</th>
                      <th>Tabela de comissão</th>
                      <th>À vista</th>
                      <th>A prazo</th>
                      <th class="text-center">Meta equipe</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="item in plano.vendedores"
                      :key="item.id_vendedor"
                    >
                      <td class="font-weight-bolder">
                        {{ vendedorNome(item.id_vendedor) }}
                      </td>
                      <td class="text-center">
                        <b-form-checkbox
                          v-model="item.ativo"
                          :disabled="!isEditable"
                          switch
                        />
                      </td>
                      <td class="money-column">
                        <b-input-group prepend="R$">
                          <money
                            v-model="item.meta_individual"
                            class="form-control"
                            v-bind="configMoney"
                            :disabled="!isEditable || !item.ativo"
                          />
                        </b-input-group>
                        <small
                          v-if="
                            item.tabela_comissao_chave &&
                            Number(item.meta_individual || 0) <= 0
                          "
                          class="range-helper text-danger"
                        >
                          Informe a meta para calcular as faixas
                        </small>
                      </td>
                      <td class="commission-table-column">
                        <b-form-select
                          v-model="item.tabela_comissao_chave"
                          :disabled="!isEditable || !item.ativo"
                          :options="tabelaComissaoOptions"
                        />
                        <small
                          v-if="item.tabela_comissao_chave"
                          class="range-helper range-helper-success"
                        >
                          Comissão conforme o atingimento da meta
                        </small>
                      </td>
                      <td
                        :class="{
                          'commission-inherited': Boolean(
                            item.tabela_comissao_chave
                          ),
                        }"
                      >
                        <b-input-group append="%">
                          <b-form-input
                            v-model="item.comissao_vista"
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            :disabled="
                              !isEditable ||
                              !item.ativo ||
                              Boolean(item.tabela_comissao_chave)
                            "
                            :placeholder="
                              item.tabela_comissao_chave
                                ? 'Pela tabela'
                                : 'Global'
                            "
                          />
                        </b-input-group>
                      </td>
                      <td
                        :class="{
                          'commission-inherited': Boolean(
                            item.tabela_comissao_chave
                          ),
                        }"
                      >
                        <b-input-group append="%">
                          <b-form-input
                            v-model="item.comissao_prazo"
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            :disabled="
                              !isEditable ||
                              !item.ativo ||
                              Boolean(item.tabela_comissao_chave)
                            "
                            :placeholder="
                              item.tabela_comissao_chave
                                ? 'Pela tabela'
                                : 'Global'
                            "
                          />
                        </b-input-group>
                      </td>
                      <td class="text-center">
                        <b-form-checkbox
                          v-if="!temMetaIndividual(item)"
                          v-model="item.participa_meta_equipe"
                          :disabled="!isEditable || !item.ativo"
                          switch
                        />
                        <span
                          v-else
                          class="label label-inline label-light-primary"
                        >
                          Substituída
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </b-tab>

            <b-tab title="Faixas progressivas">
              <div class="section-heading">
                <div>
                  <h5>Tabelas progressivas de comissão</h5>
                  <p>
                    Crie os modelos White Label, Cliente Final ou outros. O
                    vínculo com cada vendedor é feito somente em Metas
                    individuais.
                  </p>
                </div>
                <button
                  v-if="isEditable"
                  type="button"
                  class="btn btn-light-primary font-weight-bold"
                  @click="adicionarTabelaComissao"
                >
                  Nova tabela
                </button>
              </div>
              <div v-if="!plano.tabelas_comissao.length" class="empty-state">
                Nenhuma tabela progressiva cadastrada. Os vendedores continuam
                usando as comissões à vista e a prazo.
              </div>
              <div
                v-for="(tabela, tabelaIndex) in plano.tabelas_comissao"
                :key="tabela._key"
                class="commission-table-card"
              >
                <div class="commission-table-header">
                  <div class="range-number">{{ tabelaIndex + 1 }}</div>
                  <div class="commission-table-identity">
                    <b-form-input
                      v-model.trim="tabela.nome"
                      :disabled="!isEditable"
                      placeholder="Nome da tabela, ex.: White Label — Experiência"
                    />
                    <b-form-input
                      v-model.trim="tabela.descricao"
                      :disabled="!isEditable"
                      placeholder="Descrição opcional"
                    />
                  </div>
                  <div class="commission-table-usage">
                    <span class="label label-inline label-light-primary">
                      {{ quantidadeVendedoresTabela(tabela._key) }}
                      vendedor(es)
                    </span>
                    <button
                      v-if="isEditable"
                      type="button"
                      class="btn btn-sm btn-icon btn-light-danger"
                      title="Remover tabela"
                      @click="removerTabelaComissao(tabelaIndex)"
                    >
                      <b-icon icon="trash" />
                    </button>
                  </div>
                </div>

                <div class="commission-tier-heading">
                  <div>
                    <strong>Faixas de atingimento</strong>
                    <small>
                      Defina quanto cada modalidade paga em cada faixa.
                    </small>
                  </div>
                  <div class="commission-tier-actions">
                    <button
                      type="button"
                      class="btn btn-sm font-weight-bold commission-link-button"
                      :class="
                        tabela.vincular_percentuais
                          ? 'btn-light-primary'
                          : 'btn-light'
                      "
                      :disabled="!isEditable"
                      :title="
                        tabela.vincular_percentuais
                          ? 'Desvincular os percentuais à vista e a prazo'
                          : 'Usar no prazo os mesmos percentuais à vista'
                      "
                      @click="alternarVinculoPercentuais(tabela)"
                    >
                      <b-icon icon="link" class="mr-1" />
                      {{
                        tabela.vincular_percentuais
                          ? "Vista e prazo vinculados"
                          : "Vista e prazo independentes"
                      }}
                    </button>
                    <button
                      v-if="isEditable"
                      type="button"
                      class="btn btn-sm btn-light-primary font-weight-bold"
                      @click="adicionarFaixaTabela(tabela)"
                    >
                      Adicionar faixa
                    </button>
                  </div>
                </div>

                <div v-if="!tabela.faixas.length" class="tier-empty">
                  Adicione a primeira faixa desta tabela.
                </div>
                <div
                  v-for="(faixa, faixaIndex) in tabela.faixas"
                  :key="faixa._key"
                  class="commission-tier-row"
                >
                  <div class="tier-sequence">{{ faixaIndex + 1 }}</div>
                  <b-form-group label="De">
                    <b-input-group append="%">
                      <b-form-input
                        v-model.number="faixa.percentual_inicial"
                        type="number"
                        min="0"
                        step="1"
                        :disabled="!isEditable || faixaIndex > 0"
                        @change="
                          onFaixaTabelaInicioAlterado(tabela, faixa, faixaIndex)
                        "
                        @wheel="desfocarCampoNumerico"
                      />
                    </b-input-group>
                    <small v-if="faixaIndex > 0" class="range-helper">
                      Até anterior + 1
                    </small>
                  </b-form-group>
                  <b-form-group label="Até">
                    <b-input-group append="%">
                      <b-form-input
                        v-model.number="faixa.percentual_final"
                        type="number"
                        :min="Number(faixa.percentual_inicial || 0) + 1"
                        step="1"
                        placeholder="Sem limite"
                        :disabled="!isEditable"
                        @change="
                          onFaixaTabelaLimiteAlterado(tabela, faixa, faixaIndex)
                        "
                        @wheel="desfocarCampoNumerico"
                      />
                    </b-input-group>
                    <small
                      v-if="
                        faixa.percentual_final !== null &&
                        faixa.percentual_final !== ''
                      "
                      class="range-helper range-helper-success"
                    >
                      Próxima em
                      {{ percent(Number(faixa.percentual_final) + 1) }}
                    </small>
                  </b-form-group>
                  <b-form-group label="Comissão à vista">
                    <b-input-group append="%">
                      <b-form-input
                        v-model.number="faixa.comissao_percentual"
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        :disabled="!isEditable"
                        @input="sincronizarPercentualPrazo(tabela, faixa)"
                        @wheel="desfocarCampoNumerico"
                      />
                    </b-input-group>
                  </b-form-group>
                  <b-form-group label="Comissão a prazo">
                    <b-input-group append="%">
                      <b-form-input
                        v-model.number="faixa.comissao_percentual_prazo"
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        :disabled="!isEditable || tabela.vincular_percentuais"
                        @wheel="desfocarCampoNumerico"
                      />
                    </b-input-group>
                    <small
                      v-if="tabela.vincular_percentuais"
                      class="range-helper"
                    >
                      Mesmo percentual à vista
                    </small>
                  </b-form-group>
                  <div class="tier-result">
                    <span>Ao atingir esta faixa</span>
                    <strong>
                      {{
                        percentualFaixaLabel(faixa, tabela.vincular_percentuais)
                      }}
                    </strong>
                  </div>
                  <button
                    v-if="isEditable"
                    type="button"
                    class="btn btn-sm btn-icon btn-light-danger"
                    title="Remover faixa"
                    @click="removerFaixaTabela(tabela, faixaIndex)"
                  >
                    <b-icon icon="trash" />
                  </button>
                </div>
              </div>
            </b-tab>

            <b-tab title="Formas de pagamento">
              <div class="section-heading">
                <div>
                  <h5>Classificação à vista ou a prazo</h5>
                  <p>
                    Esta classificação elimina interpretações por nome e permite
                    calcular corretamente vendas com pagamentos mistos.
                  </p>
                </div>
              </div>
              <div class="payment-grid">
                <div
                  v-for="forma in formasPagamentoComClassificacao"
                  :key="forma.id_forma_pagamento"
                  class="payment-item"
                >
                  <span class="font-weight-bolder">{{ forma.descricao }}</span>
                  <b-form-select
                    v-model="forma.modalidade"
                    :disabled="!isEditable"
                    :options="paymentModeOptions"
                  />
                </div>
              </div>
            </b-tab>

            <b-tab title="Apuração e financeiro">
              <div class="settlement-period-card">
                <div class="settlement-period-heading">
                  <div class="config-panel-icon is-coral">
                    <b-icon icon="calendar-check" />
                  </div>
                  <div>
                    <h6>Competência da apuração</h6>
                    <p>
                      Selecione o mês. O período é definido automaticamente do
                      primeiro ao último dia.
                    </p>
                  </div>
                  <span class="dashboard-chip ml-auto">
                    {{ eventoCompetenciaLabel }}
                  </span>
                </div>
                <b-row align-v="end">
                  <b-col md="4">
                    <b-form-group label="Mês de referência" class="mb-0">
                      <b-form-input
                        v-model="competenciaApuracao"
                        type="month"
                        @change="aplicarCompetenciaSelecionada"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col md="8" class="mt-3 mt-md-0">
                    <span class="d-block text-muted font-size-sm mb-2">
                      Período mensal fechado
                    </span>
                    <strong>
                      {{ dateBr(periodo.data_inicial) }} a
                      {{ dateBr(periodo.data_final) }}
                    </strong>
                    <small class="d-block text-muted mt-1">
                      A data não pode ser personalizada para evitar sobreposição
                      ou lacunas entre competências.
                    </small>
                  </b-col>
                </b-row>
                <div class="settlement-period-actions">
                  <button
                    type="button"
                    class="btn btn-light-primary font-weight-bold mr-2"
                    :disabled="processing"
                    @click="simular"
                  >
                    Atualizar simulação
                  </button>
                  <button
                    type="button"
                    class="btn btn-primary font-weight-bold"
                    :disabled="processing || !podeFecharApuracao"
                    :title="
                      podeFecharApuracao
                        ? ''
                        : 'Salve a configuração antes de fechar competências'
                    "
                    @click="calcularApuracao"
                  >
                    {{
                      podeFecharApuracao
                        ? "Fechar apuração"
                        : "Salve a configuração para fechar"
                    }}
                  </button>
                </div>
                <div v-if="!podeFecharApuracao" class="settlement-draft-note">
                  <b-icon icon="info-circle" class="mr-2" />
                  A simulação já pode ser conferida. Salve a configuração antes
                  de gerar o fechamento financeiro.
                </div>
              </div>

              <div v-if="resultado" class="result-panel">
                <div class="metrics-grid">
                  <div class="metric-card">
                    <span>Base comissionável</span>
                    <strong>{{ money(resultado.equipe.realizado) }}</strong>
                  </div>
                  <div class="metric-card">
                    <span>Atingimento</span>
                    <strong>{{ percent(resultado.equipe.atingimento) }}</strong>
                  </div>
                  <div class="metric-card">
                    <span>Comissão gerada</span>
                    <strong>{{ money(resultado.equipe.total_pagar) }}</strong>
                  </div>
                  <div class="metric-card">
                    <span>Aguardando recebimento</span>
                    <strong>{{
                      money(
                        resultado.equipe.resumo_financeiro
                          .valor_aguardando_recebimento
                      )
                    }}</strong>
                  </div>
                  <div class="metric-card featured">
                    <span>Comissão a pagar neste mês</span>
                    <strong>{{
                      money(
                        resultadoPorRecebimento
                          ? resultado.equipe.resumo_financeiro.valor_disponivel
                          : resultado.equipe.total_pagar
                      )
                    }}</strong>
                  </div>
                </div>
                <div class="table-responsive mt-5">
                  <table class="table table-head-custom table-vertical-center">
                    <thead>
                      <tr>
                        <th>Vendedor</th>
                        <th class="text-right">Realizado</th>
                        <th class="text-right">Meta</th>
                        <th class="text-right">Atingimento</th>
                        <th>Regra / faixa</th>
                        <th class="text-right">Gerada</th>
                        <th class="text-right">Bônus</th>
                        <th class="text-right">Aguardando</th>
                        <th class="text-right">A pagar</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="vendedor in resultado.vendedores"
                        :key="vendedor.id_vendedor"
                      >
                        <td class="font-weight-bolder">{{ vendedor.nome }}</td>
                        <td class="text-right">
                          {{ money(vendedor.realizado_individual) }}
                        </td>
                        <td class="text-right">
                          {{ moneyNullable(vendedor.meta_individual) }}
                        </td>
                        <td class="text-right">
                          {{ percent(vendedor.atingimento_individual) }}
                        </td>
                        <td>
                          <strong class="d-block">{{
                            vendedorRegraLabel(vendedor)
                          }}</strong>
                          <small class="text-muted">{{
                            vendedorFaixaLabel(vendedor)
                          }}</small>
                        </td>
                        <td class="text-right">
                          {{ money(vendedor.total_pagar) }}
                        </td>
                        <td class="text-right">
                          {{
                            money(
                              Number(vendedor.bonus_individual || 0) +
                                Number(vendedor.bonus_equipe || 0)
                            )
                          }}
                        </td>
                        <td class="text-right">
                          {{
                            vendedorFuture(vendedor).quantidade
                              ? money(
                                  vendedorFuture(vendedor).comissao_estimada
                                )
                              : "—"
                          }}
                        </td>
                        <td class="text-right font-weight-bolder">
                          {{
                            money(
                              resultadoPorRecebimento
                                ? vendedorFinance(vendedor).valor_disponivel
                                : vendedor.total_pagar
                            )
                          }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div class="section-heading mt-10">
                <div>
                  <h5>Competências fechadas</h5>
                  <p>Histórico auditável das apurações da empresa.</p>
                </div>
              </div>
              <div class="table-responsive">
                <table class="table table-head-custom table-vertical-center">
                  <thead>
                    <tr>
                      <th>Período</th>
                      <th>Configuração usada</th>
                      <th>Status</th>
                      <th class="text-right">Realizado</th>
                      <th class="text-right">Comissão gerada</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="!apuracoes.length">
                      <td colspan="6" class="text-center text-muted py-8">
                        Nenhuma competência fechada.
                      </td>
                    </tr>
                    <tr v-for="apuracao in apuracoes" :key="apuracao.id">
                      <td>
                        {{ dateBr(apuracao.data_inicial) }} a
                        {{ dateBr(apuracao.data_final) }}
                      </td>
                      <td>
                        {{
                          apuracao.plano
                            ? "Configuração de comissões"
                            : "Regras anteriores"
                        }}
                      </td>
                      <td>
                        <span
                          class="label label-inline"
                          :class="statusClass(apuracao.status)"
                        >
                          {{ statusLabel(apuracao.status) }}
                        </span>
                      </td>
                      <td class="text-right">
                        {{ money(apuracao.realizado_equipe) }}
                      </td>
                      <td class="text-right font-weight-bolder">
                        {{ money(apuracao.total_pagar) }}
                      </td>
                      <td class="text-right">
                        <button
                          type="button"
                          class="btn btn-sm btn-light-primary"
                          @click="carregarApuracao(apuracao.id)"
                        >
                          Detalhes
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div
                v-if="apuracaoSelecionada"
                class="settlement-detail settlement-detail-v2 mt-8"
              >
                <div class="settlement-detail-header">
                  <div>
                    <span class="section-eyebrow">Memória do fechamento</span>
                    <div class="d-flex align-items-center flex-wrap">
                      <h5 class="mb-0 mr-3">
                        Apuração #{{ apuracaoSelecionada.id }}
                      </h5>
                      <span
                        class="label label-inline"
                        :class="statusClass(apuracaoSelecionada.status)"
                      >
                        {{ statusLabel(apuracaoSelecionada.status) }}
                      </span>
                    </div>
                    <p>
                      {{ dateBr(apuracaoSelecionada.data_inicial) }} a
                      {{ dateBr(apuracaoSelecionada.data_final) }} ·
                      {{
                        apuracaoPorRecebimento
                          ? "Meta pelas vendas · financeiro após recebimento"
                          : "Meta e financeiro no fechamento"
                      }}
                    </p>
                  </div>
                  <div class="settlement-header-actions">
                    <button
                      v-if="podeCancelarApuracao"
                      type="button"
                      class="btn btn-outline-danger font-weight-bold"
                      :disabled="processing"
                      @click="cancelarApuracao"
                    >
                      <b-icon icon="x-circle" class="mr-2" />
                      Cancelar apuração
                    </button>
                    <button
                      v-if="apuracaoSelecionada.status === 'calculado'"
                      type="button"
                      class="btn btn-primary font-weight-bold"
                      :disabled="processing"
                      @click="aprovarApuracao"
                    >
                      <b-icon icon="check2-circle" class="mr-2" />
                      Aprovar apuração
                    </button>
                    <button
                      v-if="
                        ['aprovado', 'financeiro_gerado'].includes(
                          apuracaoSelecionada.status
                        ) && resumoFinanceiroApuracao.saldo_liberar > 0
                      "
                      type="button"
                      class="btn btn-primary font-weight-bold"
                      :disabled="processing"
                      @click="gerarFinanceiro"
                    >
                      <b-icon icon="wallet2" class="mr-2" />
                      Gerar financeiro de
                      {{ money(resumoFinanceiroApuracao.saldo_liberar) }}
                    </button>
                  </div>
                </div>

                <div class="settlement-overview-grid">
                  <div class="settlement-overview-item">
                    <span>Vendido comissionável</span>
                    <strong>{{
                      money(apuracaoSelecionada.realizado_equipe)
                    }}</strong>
                  </div>
                  <div class="settlement-overview-item">
                    <span>Comissão gerada pelas vendas do mês</span>
                    <strong>{{
                      money(resumoFinanceiroApuracao.valor_gerado)
                    }}</strong>
                    <small>Direito definido pela meta e faixa deste mês.</small>
                  </div>
                  <div class="settlement-overview-item">
                    <span>Comissão a ser paga neste mês</span>
                    <strong>{{
                      money(resumoFinanceiroApuracao.valor_a_pagar)
                    }}</strong>
                    <small>
                      Baixas do mês, inclusive de vendas de meses anteriores.
                    </small>
                  </div>
                  <div class="settlement-overview-item is-total">
                    <span>Aguardando recebimento</span>
                    <strong>{{
                      money(
                        resumoFinanceiroApuracao.valor_aguardando_recebimento
                      )
                    }}</strong>
                    <small>Já gerada, mas ainda fora do financeiro.</small>
                  </div>
                </div>

                <div class="seller-settlement-list">
                  <article
                    v-for="vendedor in apuracaoSelecionada.vendedores"
                    :key="vendedor.id"
                    class="seller-settlement-card"
                  >
                    <header class="seller-settlement-header">
                      <div class="seller-avatar">
                        {{ initials(vendedor.vendedor_nome) }}
                      </div>
                      <div>
                        <h6>{{ vendedor.vendedor_nome }}</h6>
                        <p>
                          {{ vendedorRegraLabel(vendedor) }}
                        </p>
                      </div>
                      <div class="seller-settlement-tags">
                        <span class="seller-rule-chip">
                          {{ vendedorFaixaLabel(vendedor) }}
                        </span>
                        <span
                          class="label label-inline"
                          :class="sellerFinanceStatusClass(vendedor)"
                        >
                          {{ sellerFinanceStatusLabel(vendedor) }}
                        </span>
                      </div>
                    </header>

                    <div class="seller-settlement-grid">
                      <section class="seller-performance-panel">
                        <span class="seller-panel-label">Desempenho</span>
                        <div class="seller-primary-value">
                          {{ money(vendedor.realizado_individual) }}
                        </div>
                        <div class="seller-goal-row">
                          <span>
                            Meta
                            {{ moneyNullable(vendedor.meta_individual) }}
                          </span>
                          <strong>{{
                            percent(vendedor.atingimento_individual)
                          }}</strong>
                        </div>
                        <div class="seller-progress">
                          <span
                            :style="{
                              width: progressWidth(
                                vendedor.atingimento_individual
                              ),
                            }"
                          ></span>
                        </div>
                        <div class="seller-rule-result">
                          <span>Percentual aplicado</span>
                          <strong>{{
                            vendedorPercentualLabel(vendedor)
                          }}</strong>
                        </div>
                      </section>

                      <section class="seller-commission-panel">
                        <span class="seller-panel-label"
                          >Composição da comissão</span
                        >
                        <div class="commission-breakdown-row">
                          <span>
                            À vista · {{ money(vendedor.total_vista) }}
                          </span>
                          <strong>{{ money(vendedor.comissao_vista) }}</strong>
                        </div>
                        <div class="commission-breakdown-row">
                          <span>
                            A prazo · {{ money(vendedor.total_prazo) }}
                          </span>
                          <strong>{{ money(vendedor.comissao_prazo) }}</strong>
                        </div>
                        <div class="commission-breakdown-row">
                          <span>Bônus</span>
                          <strong>{{
                            money(
                              Number(vendedor.bonus_individual || 0) +
                                Number(vendedor.bonus_equipe || 0)
                            )
                          }}</strong>
                        </div>
                        <div class="commission-breakdown-total">
                          <span>Comissão gerada</span>
                          <strong>{{ money(vendedor.total_pagar) }}</strong>
                        </div>
                      </section>

                      <aside class="seller-finance-panel">
                        <span class="seller-panel-label"
                          >Pagamento da competência</span
                        >
                        <div class="finance-status-icon">
                          <b-icon
                            :icon="
                              vendedorPayment(vendedor).saldo_liberar <= 0
                                ? 'check-circle'
                                : 'wallet2'
                            "
                          />
                        </div>
                        <strong>
                          {{ money(vendedorPayment(vendedor).valor_a_pagar) }}
                          a pagar
                        </strong>
                        <p>
                          {{ money(vendedorPayment(vendedor).valor_gerado) }}
                          gerados pelas vendas ·
                          {{ vendedorPayment(vendedor).quantidade }} item(ns)
                          para pagamento
                        </p>
                        <small
                          v-if="vendedorPayment(vendedor).valor_liberado > 0"
                          class="d-block text-muted mb-3"
                        >
                          {{ money(vendedorPayment(vendedor).valor_liberado) }}
                          já gerados no Contas a Pagar
                        </small>
                        <small
                          v-if="(vendedor.liberacoes || []).length"
                          class="d-block text-muted mb-3"
                        >
                          Contas:
                          {{ sellerAccountLabels(vendedor) }}
                        </small>
                        <button
                          type="button"
                          class="btn btn-sm btn-light-primary btn-block"
                          :disabled="pdfSellerId === vendedor.id_vendedor"
                          @click="abrirPdfVendedor(vendedor)"
                        >
                          <b-spinner
                            v-if="pdfSellerId === vendedor.id_vendedor"
                            small
                            class="mr-2"
                          />
                          <b-icon v-else icon="file-earmark-pdf" class="mr-2" />
                          Abrir PDF
                        </button>
                        <button
                          type="button"
                          class="btn btn-sm btn-link btn-block"
                          @click="toggleSellerDetails(vendedor.id)"
                        >
                          {{
                            sellerDetailsVisible(vendedor.id)
                              ? "Ocultar lançamentos"
                              : "Ver vendas e cálculo"
                          }}
                          <b-icon
                            :icon="
                              sellerDetailsVisible(vendedor.id)
                                ? 'chevron-up'
                                : 'chevron-down'
                            "
                            class="ml-1"
                          />
                        </button>
                      </aside>
                    </div>

                    <div
                      v-if="
                        apuracaoPorRecebimento &&
                        vendedorFuture(vendedor).quantidade > 0
                      "
                      class="seller-future-strip"
                    >
                      <b-icon icon="hourglass-split" />
                      <div>
                        <strong>
                          {{ vendedorFuture(vendedor).quantidade }} título(s)
                          aguardando recebimento
                        </strong>
                        <span>
                          Base futura de
                          {{ money(vendedorFuture(vendedor).valor_base) }}.
                          {{
                            vendedorFuture(vendedor).estimativa_disponivel
                              ? "Estimativa de " +
                                money(
                                  vendedorFuture(vendedor).comissao_estimada
                                ) +
                                "."
                              : "Comissão já definida pela faixa do mês."
                          }}
                        </span>
                      </div>
                    </div>

                    <div
                      v-if="sellerDetailsVisible(vendedor.id)"
                      class="seller-ledger"
                    >
                      <div class="payment-ledger">
                        <div class="seller-ledger-heading">
                          <div>
                            <strong>Comissão a ser paga neste mês</strong>
                            <span>
                              Usa a data da baixa. A meta e a taxa continuam
                              sendo as da competência original da venda.
                            </span>
                          </div>
                          <span class="payment-ledger-total">
                            {{ money(vendedorPayment(vendedor).valor_a_pagar) }}
                          </span>
                        </div>
                        <div class="table-responsive">
                          <table
                            class="table table-head-custom table-vertical-center mb-0"
                          >
                            <thead>
                              <tr>
                                <th>Data do evento</th>
                                <th>Documento / cliente</th>
                                <th>Origem da venda</th>
                                <th class="text-right">Base recebida</th>
                                <th class="text-right">Taxa original</th>
                                <th class="text-right">A pagar</th>
                                <th>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr
                                v-if="!vendedorPayment(vendedor).itens.length"
                              >
                                <td
                                  colspan="7"
                                  class="text-center text-muted py-6"
                                >
                                  Nenhuma comissão foi liberada para pagamento
                                  nesta competência.
                                </td>
                              </tr>
                              <tr
                                v-for="item in vendedorPayment(vendedor).itens"
                                :key="item.origem_chave"
                              >
                                <td>{{ dateBr(item.data_evento) }}</td>
                                <td>
                                  <strong>{{ item.documento || "—" }}</strong>
                                  <small class="d-block text-muted">
                                    {{ item.cliente || "Sem cliente" }}
                                  </small>
                                </td>
                                <td>
                                  <strong>
                                    {{ origemLabel(item.origem_tipo) }}
                                  </strong>
                                  <small class="d-block text-muted">
                                    Venda de
                                    {{ monthYear(item.competencia_origem) }}
                                  </small>
                                </td>
                                <td class="text-right">
                                  {{ money(item.valor_base) }}
                                </td>
                                <td class="text-right">
                                  {{ percent(item.percentual_aplicado) }}
                                </td>
                                <td class="text-right font-weight-bolder">
                                  {{ money(item.valor_comissao) }}
                                </td>
                                <td>
                                  <span
                                    class="label label-inline"
                                    :class="
                                      item.id_liberacao
                                        ? 'label-light-success'
                                        : 'label-light-primary'
                                    "
                                  >
                                    {{
                                      item.id_liberacao
                                        ? "Financeiro gerado"
                                        : "A gerar"
                                    }}
                                  </span>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <template v-if="apuracaoPorRecebimento">
                        <div class="seller-ledger-heading">
                          <div>
                            <strong
                              >Vendas que definiram meta e comissão</strong
                            >
                            <span>
                              Cada linha mostra a base, o percentual e o valor
                              calculado.
                            </span>
                          </div>
                        </div>
                        <div class="table-responsive">
                          <table
                            class="table table-head-custom table-vertical-center mb-0"
                          >
                            <thead>
                              <tr>
                                <th>Data</th>
                                <th>Documento / cliente</th>
                                <th>Origem</th>
                                <th>Modalidade</th>
                                <th class="text-right">Base</th>
                                <th class="text-right">%</th>
                                <th class="text-right">Comissão</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-if="!vendedor.lancamentos.length">
                                <td
                                  colspan="7"
                                  class="text-center text-muted py-6"
                                >
                                  Nenhum lançamento comissionável nesta
                                  competência.
                                </td>
                              </tr>
                              <tr
                                v-for="lancamento in vendedor.lancamentos"
                                :key="lancamento.id"
                              >
                                <td>
                                  {{ dateBr(lancamento.data_competencia) }}
                                </td>
                                <td>
                                  <strong>{{
                                    lancamento.documento ||
                                    lancamento.descricao ||
                                    "—"
                                  }}</strong>
                                  <small class="d-block text-muted">
                                    {{ lancamento.cliente || "Sem cliente" }}
                                  </small>
                                </td>
                                <td>
                                  {{ origemLabel(lancamento.origem_tipo) }}
                                </td>
                                <td>
                                  {{ modalidadeLabel(lancamento.modalidade) }}
                                </td>
                                <td class="text-right">
                                  {{ money(lancamento.valor_base) }}
                                </td>
                                <td class="text-right">
                                  {{ percent(lancamento.percentual_aplicado) }}
                                </td>
                                <td class="text-right font-weight-bolder">
                                  {{ money(lancamento.valor_calculado) }}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </template>

                      <div
                        v-if="
                          apuracaoPorRecebimento &&
                          vendedorFuture(vendedor).quantidade > 0
                        "
                        class="future-ledger"
                      >
                        <div class="seller-ledger-heading">
                          <div>
                            <strong>Vendas ainda não recebidas</strong>
                            <span>
                              Estas vendas já compõem a meta e a faixa; somente
                              a liberação financeira aguarda o recebimento.
                            </span>
                          </div>
                        </div>
                        <div class="table-responsive">
                          <table
                            class="table table-head-custom table-vertical-center mb-0"
                          >
                            <thead>
                              <tr>
                                <th>Venda</th>
                                <th>Documento / cliente</th>
                                <th>Vencimento</th>
                                <th class="text-right">Base futura</th>
                                <th class="text-right">Comissão</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr
                                v-for="item in vendedorFuture(vendedor).itens"
                                :key="item.chave"
                              >
                                <td>{{ dateBr(item.data_venda) }}</td>
                                <td>
                                  <strong>{{ item.documento }}</strong>
                                  <small class="d-block text-muted">
                                    {{ item.cliente || "Sem cliente" }}
                                  </small>
                                </td>
                                <td>{{ dateBr(item.data_vencimento) }}</td>
                                <td class="text-right">
                                  {{ money(item.valor_base) }}
                                </td>
                                <td class="text-right">
                                  {{
                                    item.comissao_estimada === null
                                      ? "A definir"
                                      : money(item.comissao_estimada)
                                  }}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </article>
                </div>
              </div>
            </b-tab>
          </b-tabs>
        </div>

        <div
          v-if="tabIndex > 0 && tabIndex < 5"
          class="card-footer text-center"
        >
          <button
            type="button"
            class="btn btn-primary font-weight-bold"
            :disabled="saving"
            @click="salvarPlano"
          >
            <b-spinner v-if="saving" small class="mr-2" />
            Salvar configurações
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { mapActions, mapGetters } from "vuex";
import { Money } from "v-money";
import { MONEY as settingMoney, SistemaMixin } from "@/modules/Application";
import { FEATURES } from "@/core/constants/features";

export default {
  name: "app-vendedor-comissoes",
  mixins: [SistemaMixin],
  components: {
    Money,
  },
  data() {
    const inicioMes = moment().startOf("month").format("YYYY-MM-DD");
    const fimMes = moment().endOf("month").format("YYYY-MM-DD");

    return {
      loading: true,
      saving: false,
      processing: false,
      tabIndex: 0,
      configMoney: settingMoney,
      plano: {
        vendedores: [],
        faixas: [],
        tabelas_comissao: [],
        formas_pagamento: [],
      },
      vendedores: [],
      formasPagamento: [],
      resultado: null,
      historico: {
        agrupamento: "semana",
        periodos: [],
      },
      agrupamentoHistorico: "auto",
      apuracoes: [],
      apuracaoSelecionada: null,
      pdfSellerId: null,
      sellerDetailsOpen: {},
      competenciaApuracao: moment().format("YYYY-MM"),
      periodo: {
        data_inicial: inicioMes,
        data_final: fimMes,
        id_vendedor: null,
      },
      bonusTypeOptions: [
        { value: null, text: "Sem bônus" },
        { value: "fixo", text: "Valor fixo" },
        {
          value: "percentual_comissao",
          text: "% sobre a comissão base",
        },
        {
          value: "pontos_percentuais",
          text: "Pontos % sobre as vendas",
        },
      ],
      paymentModeOptions: [
        { value: "vista", text: "À vista" },
        { value: "prazo", text: "A prazo" },
        { value: "ignorar", text: "Não comissionar" },
      ],
      periodShortcuts: [
        { value: "mes_atual", text: "Este mês" },
        { value: "90_dias", text: "90 dias" },
        { value: "6_meses", text: "6 meses" },
        { value: "12_meses", text: "12 meses" },
      ],
      historyGroupingOptions: [
        { value: "auto", text: "Automático" },
        { value: "semana", text: "Semanas" },
        { value: "mes", text: "Meses" },
      ],
    };
  },
  computed: {
    ...mapGetters(["itsblocked"]),
    hasModuloServicos() {
      return this.$store.getters.hasFeature(FEATURES.MOD_SERVICOS);
    },
    isEditable() {
      return true;
    },
    podeFecharApuracao() {
      return this.plano.status === "ativo";
    },
    podeCancelarApuracao() {
      if (
        !this.apuracaoSelecionada ||
        !["calculado", "aprovado"].includes(this.apuracaoSelecionada.status)
      ) {
        return false;
      }

      return (
        Number(this.resumoFinanceiroApuracao.valor_liberado || 0) <= 0 &&
        !(this.apuracaoSelecionada.vendedores || []).some(
          (vendedor) =>
            vendedor.id_conta_pagar ||
            (vendedor.liberacoes || []).some(
              (liberacao) => liberacao.id_conta_pagar
            )
        )
      );
    },
    primaryBrandColor() {
      if (typeof window === "undefined") return "#ee7361";

      return (
        window
          .getComputedStyle(document.documentElement)
          .getPropertyValue("--primary-color")
          .trim() || "#ee7361"
      );
    },
    sellerOptions() {
      return [
        { value: null, text: "Toda a equipe" },
        ...this.vendedores.map((vendedor) => ({
          value: vendedor.id_vendedor,
          text: vendedor.nome,
        })),
      ];
    },
    tabelaComissaoOptions() {
      return [
        {
          value: null,
          text: "Padrão à vista e a prazo",
        },
        ...(this.plano.tabelas_comissao || []).map((tabela) => ({
          value: tabela._key,
          text: tabela.nome || "Tabela sem nome",
        })),
      ];
    },
    formasPagamentoComClassificacao() {
      return this.plano.formas_pagamento || [];
    },
    eventoCompetenciaLabel() {
      return this.plano.evento_competencia === "recebimento"
        ? "Financeiro após recebimento"
        : "Financeiro no fechamento";
    },
    resultadoPorRecebimento() {
      return Boolean(
        this.resultado &&
          this.resultado.plano &&
          this.resultado.plano.evento_competencia === "recebimento"
      );
    },
    apuracaoPorRecebimento() {
      return Boolean(
        this.apuracaoSelecionada &&
          this.apuracaoSelecionada.plano_snapshot &&
          this.apuracaoSelecionada.plano_snapshot.evento_competencia ===
            "recebimento"
      );
    },
    resumoFinanceiroApuracao() {
      const vazio = {
        condicionado_recebimento: false,
        valor_calculado: 0,
        valor_gerado: 0,
        valor_a_pagar: 0,
        valor_disponivel: 0,
        valor_aguardando_recebimento: 0,
        valor_liberado: 0,
        saldo_liberar: 0,
        saldo_total: 0,
      };
      if (!this.apuracaoSelecionada) return vazio;

      return {
        ...vazio,
        valor_calculado: Number(this.apuracaoSelecionada.total_pagar || 0),
        valor_gerado: Number(this.apuracaoSelecionada.total_pagar || 0),
        ...(this.apuracaoSelecionada.resumo_financeiro || {}),
      };
    },
    resumoVisivel() {
      if (!this.resultado) return null;
      if (!this.periodo.id_vendedor) return this.resultado.equipe;

      const vendedor = (this.resultado.vendedores || [])[0];
      if (!vendedor) {
        return {
          meta: null,
          realizado: 0,
          atingimento: 0,
          total_comissao_base: 0,
          total_bonus: 0,
          total_ajustes: 0,
          total_pagar: 0,
          resumo_financeiro: {
            valor_disponivel: 0,
            valor_aguardando_recebimento: 0,
          },
        };
      }

      return {
        meta: vendedor.meta_individual,
        realizado: vendedor.realizado_individual,
        atingimento: vendedor.atingimento_individual,
        total_comissao_base: vendedor.comissao_base,
        total_bonus:
          Number(vendedor.bonus_individual || 0) +
          Number(vendedor.bonus_equipe || 0),
        total_ajustes: vendedor.ajustes,
        total_pagar: vendedor.total_pagar,
        resumo_financeiro: this.vendedorFinance(vendedor),
      };
    },
    rankingVendedores() {
      if (!this.resultado) return [];

      const realizadoEquipe = (this.resultado.vendedores || []).reduce(
        (total, vendedor) => total + Number(vendedor.realizado_individual || 0),
        0
      );
      return [...(this.resultado.vendedores || [])]
        .map((vendedor) => ({
          ...vendedor,
          participacao_equipe:
            realizadoEquipe > 0
              ? (Number(vendedor.realizado_individual || 0) / realizadoEquipe) *
                100
              : 0,
        }))
        .sort(
          (primeiro, segundo) =>
            Number(segundo.realizado_individual || 0) -
            Number(primeiro.realizado_individual || 0)
        );
    },
    overviewCards() {
      if (!this.resultado) return [];

      const equipe = this.resumoVisivel || {};
      const financeiro = equipe.resumo_financeiro || {};
      const vendedoresComMeta = this.rankingVendedores.filter(
        (vendedor) => Number(vendedor.meta_individual || 0) > 0
      );
      const metasAtingidas = vendedoresComMeta.filter(
        (vendedor) => Number(vendedor.atingimento_individual || 0) >= 100
      ).length;
      const semMetaEquipe =
        equipe.meta === null ||
        equipe.meta === undefined ||
        Number(equipe.meta) <= 0;

      return [
        {
          titulo: "Base comissionável",
          valor: this.money(equipe.realizado),
          legenda: "Vendido no período",
          icone: "cash-stack",
          classe: "bg-light-primary text-primary",
        },
        {
          titulo: this.periodo.id_vendedor
            ? "Meta individual"
            : "Meta da equipe",
          valor: semMetaEquipe ? "Sem meta" : this.money(equipe.meta),
          legenda: semMetaEquipe
            ? "Meta global não configurada"
            : this.percent(equipe.atingimento) + " de atingimento",
          icone: "bullseye",
          classe: "bg-light-success text-success",
        },
        {
          titulo: this.resultadoPorRecebimento
            ? "Comissão disponível"
            : "Comissão prevista",
          valor: this.money(
            this.resultadoPorRecebimento
              ? financeiro.valor_disponivel
              : equipe.total_pagar
          ),
          legenda: this.resultadoPorRecebimento
            ? this.money(financeiro.valor_aguardando_recebimento) +
              " aguardando recebimento"
            : this.money(equipe.total_comissao_base) + " de comissão base",
          icone: "wallet2",
          classe: "bg-light-info text-info",
        },
        {
          titulo: "Metas individuais",
          valor:
            vendedoresComMeta.length > 0
              ? metasAtingidas + " de " + vendedoresComMeta.length
              : "Sem metas",
          legenda: this.rankingVendedores.length + " vendedor(es) na análise",
          icone: "award",
          classe: "bg-light-warning text-warning",
        },
      ];
    },
    periodoRapidoAtivo() {
      const atual = {
        data_inicial: this.periodo.data_inicial,
        data_final: this.periodo.data_final,
      };
      const encontrado = this.periodShortcuts.find((atalho) => {
        const intervalo = this.datasPeriodoRapido(atalho.value);
        return (
          intervalo.data_inicial === atual.data_inicial &&
          intervalo.data_final === atual.data_final
        );
      });

      return encontrado ? encontrado.value : null;
    },
    historicoPeriodos() {
      return this.historico && Array.isArray(this.historico.periodos)
        ? this.historico.periodos
        : [];
    },
    historicoLabels() {
      return this.historicoPeriodos.map((periodo) =>
        this.rotuloPeriodoHistorico(periodo)
      );
    },
    temHistorico() {
      return this.historicoPeriodos.some(
        (periodo) =>
          Number(periodo.realizado || 0) !== 0 ||
          Number(
            this.resultadoPorRecebimento
              ? periodo.valor_disponivel
              : periodo.total_pagar
          ) !== 0
      );
    },
    seriesGraficoEvolucao() {
      return [
        {
          name: "Base comissionável",
          type: "column",
          data: this.historicoPeriodos.map((periodo) =>
            Number(periodo.realizado || 0)
          ),
        },
        {
          name: this.resultadoPorRecebimento
            ? "Comissão disponível"
            : "Comissão prevista",
          type: "line",
          data: this.historicoPeriodos.map((periodo) =>
            Number(
              this.resultadoPorRecebimento
                ? periodo.valor_disponivel || 0
                : periodo.total_pagar || 0
            )
          ),
        },
      ];
    },
    opcoesGraficoEvolucao() {
      return {
        chart: {
          type: "line",
          toolbar: { show: true },
          zoom: { enabled: true },
        },
        colors: ["#3699ff", this.primaryBrandColor],
        stroke: { width: [0, 3], curve: "smooth" },
        dataLabels: { enabled: false },
        xaxis: { categories: this.historicoLabels },
        yaxis: [
          {
            title: { text: "Base comissionável" },
            labels: { formatter: (valor) => this.moneyCompact(valor) },
          },
          {
            opposite: true,
            title: { text: "Comissão" },
            labels: { formatter: (valor) => this.moneyCompact(valor) },
          },
        ],
        tooltip: {
          y: [
            { formatter: (valor) => this.money(valor) },
            { formatter: (valor) => this.money(valor) },
          ],
        },
        legend: { position: "top" },
      };
    },
    seriesGraficoModalidades() {
      return [
        {
          name: "À vista",
          data: this.historicoPeriodos.map((periodo) =>
            Number(periodo.total_vista || 0)
          ),
        },
        {
          name: "A prazo",
          data: this.historicoPeriodos.map((periodo) =>
            Number(periodo.total_prazo || 0)
          ),
        },
      ];
    },
    opcoesGraficoModalidades() {
      return {
        chart: {
          type: "bar",
          stacked: true,
          toolbar: { show: false },
        },
        colors: [this.primaryBrandColor, "#8950fc"],
        plotOptions: {
          bar: { borderRadius: 3, columnWidth: "58%" },
        },
        dataLabels: { enabled: false },
        xaxis: {
          categories: this.historicoLabels,
          labels: { rotate: -45 },
        },
        yaxis: {
          labels: { formatter: (valor) => this.moneyCompact(valor) },
        },
        tooltip: {
          y: { formatter: (valor) => this.money(valor) },
        },
        legend: { position: "top" },
      };
    },
    seriesGraficoDesempenho() {
      const series = [
        {
          name: "Realizado",
          data: this.rankingVendedores.map((vendedor) =>
            Number(vendedor.realizado_individual || 0)
          ),
        },
      ];
      if (
        this.rankingVendedores.some(
          (vendedor) => Number(vendedor.meta_individual || 0) > 0
        )
      ) {
        series.push({
          name: "Meta individual",
          data: this.rankingVendedores.map((vendedor) =>
            Number(vendedor.meta_individual || 0)
          ),
        });
      }

      return series;
    },
    opcoesGraficoDesempenho() {
      return {
        chart: {
          type: "bar",
          toolbar: { show: false },
        },
        plotOptions: { bar: { horizontal: true } },
        colors: [this.primaryBrandColor, "#e4e6ef"],
        dataLabels: { enabled: false },
        xaxis: {
          categories: this.rankingVendedores.map((vendedor) => vendedor.nome),
          labels: {
            formatter: (valor) => this.moneyCompact(valor),
          },
        },
        tooltip: {
          y: {
            formatter: (valor) => this.money(valor),
          },
        },
      };
    },
    seriesGraficoComposicao() {
      if (!this.resultado) return [];
      const equipe = this.resumoVisivel || {};

      return [
        Math.max(0, Number(equipe.total_comissao_base || 0)),
        Math.max(0, Number(equipe.total_bonus || 0)),
      ];
    },
    temComissaoComposicao() {
      return this.seriesGraficoComposicao.some((valor) => valor > 0);
    },
    opcoesGraficoComposicao() {
      return {
        labels: ["Comissão base", "Bônus"],
        colors: [this.primaryBrandColor, "#1bc5bd"],
        legend: { position: "bottom" },
        dataLabels: { enabled: false },
        stroke: { colors: ["#ffffff"] },
        tooltip: {
          y: {
            formatter: (valor) => this.money(valor),
          },
        },
        plotOptions: {
          pie: {
            donut: {
              size: "72%",
              labels: {
                show: true,
                name: { show: true },
                value: {
                  show: true,
                  formatter: (valor) => this.money(valor),
                },
                total: {
                  show: true,
                  label: "Potencial calculado",
                  formatter: () => this.money(this.resumoVisivel.total_pagar),
                },
              },
            },
          },
        },
      };
    },
  },
  methods: {
    ...mapActions(["setLoading"]),
    planoNovo() {
      const vendedores = this.vendedores || [];

      return {
        id: null,
        nome: "Configuração de comissões",
        status: "rascunho",
        inicio_vigencia: moment().startOf("month").format("YYYY-MM-DD"),
        fim_vigencia: null,
        periodicidade: "mensal",
        evento_competencia: "faturamento",
        comissao_vista: 0,
        comissao_prazo: 0,
        incluir_nfe: true,
        incluir_nfce: true,
        incluir_pedidos: true,
        incluir_os: true,
        incluir_contratos: false,
        meta_equipe: 0,
        bonus_equipe_tipo: null,
        bonus_equipe_valor: 0,
        bonus_equipe_cumulativo: true,
        id_plano_contas: null,
        vencimento_dias: 0,
        vendedores: vendedores.map((vendedor) => ({
          id_vendedor: vendedor.id_vendedor,
          ativo: true,
          comissao_vista: Number(vendedor.comissao_vista || 0),
          comissao_prazo: Number(vendedor.comissao_prazo || 0),
          meta_individual: 0,
          tabela_comissao_chave: null,
          bonus_individual_tipo: null,
          bonus_individual_valor: 0,
          participa_meta_equipe: true,
        })),
        faixas: [],
        tabelas_comissao: [],
        formas_pagamento: this.formasPagamento.map((forma) => ({
          id_forma_pagamento: forma.id,
          descricao: forma.descricao,
          modalidade: this.inferirModalidade(forma.descricao),
        })),
      };
    },
    normalizarPlano(plano) {
      const regrasVendedor = (plano.vendedores || []).reduce((mapa, regra) => {
        mapa[Number(regra.id_vendedor)] = regra;
        return mapa;
      }, {});
      const classificacoes = (plano.formas_pagamento || []).reduce(
        (mapa, forma) => {
          mapa[Number(forma.id_forma_pagamento)] = forma.modalidade;
          return mapa;
        },
        {}
      );
      const tabelasComissao = (plano.tabelas_comissao || []).map(
        (tabela, tabelaIndex) => {
          const chave = String(
            tabela.chave ||
              tabela.id ||
              "tabela-" + Date.now() + "-" + tabelaIndex
          );
          return {
            ...tabela,
            _key: chave,
            chave,
            nome: tabela.nome || "",
            descricao: tabela.descricao || "",
            vincular_percentuais: tabela.vincular_percentuais !== false,
            ativo: tabela.ativo !== false,
            faixas: this.organizarFaixasTabela(
              (tabela.faixas || []).map((faixa, faixaIndex) => ({
                ...faixa,
                _key: String(
                  faixa.id ||
                    "faixa-" + Date.now() + "-" + tabelaIndex + "-" + faixaIndex
                ),
                percentual_inicial: Number(faixa.percentual_inicial || 0),
                percentual_final: this.inputNullable(faixa.percentual_final),
                comissao_percentual: Number(
                  faixa.comissao_percentual || faixa.recompensa_valor || 0
                ),
                comissao_percentual_prazo: Number(
                  faixa.comissao_percentual_prazo ??
                    faixa.recompensa_valor_prazo ??
                    faixa.comissao_percentual ??
                    faixa.recompensa_valor ??
                    0
                ),
                ativo: faixa.ativo !== false,
              }))
            ),
          };
        }
      );

      return {
        ...plano,
        periodicidade: "mensal",
        evento_competencia: plano.evento_competencia || "faturamento",
        meta_equipe: Number(plano.meta_equipe || 0),
        bonus_equipe_valor: Number(plano.bonus_equipe_valor || 0),
        vencimento_dias: 0,
        vendedores: this.vendedores.map((vendedor) => {
          const regra = regrasVendedor[Number(vendedor.id_vendedor)];
          const temRegra = Boolean(regra);

          return {
            id_vendedor: vendedor.id_vendedor,
            ativo:
              !temRegra || regra.ativo === undefined || regra.ativo === null
                ? true
                : Boolean(regra.ativo),
            comissao_vista: temRegra
              ? this.inputNullable(regra.comissao_vista)
              : Number(vendedor.comissao_vista || 0),
            comissao_prazo: temRegra
              ? this.inputNullable(regra.comissao_prazo)
              : Number(vendedor.comissao_prazo || 0),
            meta_individual: Number((regra && regra.meta_individual) || 0),
            tabela_comissao_chave:
              regra && regra.id_tabela_comissao
                ? String(regra.id_tabela_comissao)
                : null,
            bonus_individual_tipo:
              (regra && regra.bonus_individual_tipo) || null,
            bonus_individual_valor: Number(
              (regra && regra.bonus_individual_valor) || 0
            ),
            participa_meta_equipe:
              !temRegra ||
              regra.participa_meta_equipe === undefined ||
              regra.participa_meta_equipe === null
                ? true
                : Boolean(regra.participa_meta_equipe),
          };
        }),
        faixas: [],
        tabelas_comissao: tabelasComissao,
        formas_pagamento: this.formasPagamento.map((forma) => ({
          id_forma_pagamento: forma.id,
          descricao: forma.descricao,
          modalidade:
            classificacoes[Number(forma.id)] ||
            this.inferirModalidade(forma.descricao),
        })),
      };
    },
    async carregarTudo() {
      this.loading = true;
      this.setLoading(true);
      try {
        const resultados = await Promise.allSettled([
          this.comTimeout(this.$http.post("/vendedor/get-vendedor")),
          this.comTimeout(this.getFormasPagamentos()),
          this.comTimeout(this.$http.get("/comissoes/configuracao")),
          this.comTimeout(this.$http.get("/comissoes/apuracoes")),
        ]);
        const [
          vendedoresResult,
          formasResult,
          configuracaoResult,
          apuracoesResult,
        ] = resultados;

        this.vendedores =
          vendedoresResult.status === "fulfilled"
            ? vendedoresResult.value.data || []
            : [];
        this.formasPagamento =
          formasResult.status === "fulfilled" &&
          Array.isArray(formasResult.value)
            ? formasResult.value
            : [];
        this.apuracoes =
          apuracoesResult.status === "fulfilled"
            ? apuracoesResult.value.data.data || []
            : [];

        const planoInicial =
          configuracaoResult.status === "fulfilled"
            ? configuracaoResult.value.data.data
            : null;

        this.plano = planoInicial
          ? this.normalizarPlano(planoInicial)
          : this.planoNovo();

        const falhasComissao = [configuracaoResult, apuracoesResult].filter(
          (resultado) => resultado.status === "rejected"
        );
        if (falhasComissao.length) {
          this.$toast.error(
            "A configuração local foi aberta, mas a API de comissões não respondeu. Recarregue a página após atualizar o backend."
          );
        }
      } catch (error) {
        this.plano = this.planoNovo();
        this.showError(error, "Não foi possível carregar as comissões.");
      } finally {
        this.loading = false;
        this.setLoading(false);
      }
    },
    comTimeout(promise, milliseconds = 10000) {
      return Promise.race([
        promise,
        new Promise((resolve, reject) => {
          setTimeout(
            () => reject(new Error("Tempo limite ao carregar comissões.")),
            milliseconds
          );
        }),
      ]);
    },
    payloadPlano() {
      return {
        nome: this.plano.nome,
        inicio_vigencia: this.plano.inicio_vigencia,
        fim_vigencia: this.plano.fim_vigencia || null,
        periodicidade: "mensal",
        evento_competencia: this.plano.evento_competencia || "faturamento",
        comissao_vista: Number(this.plano.comissao_vista || 0),
        comissao_prazo: Number(this.plano.comissao_prazo || 0),
        incluir_nfe: Boolean(this.plano.incluir_nfe),
        incluir_nfce: Boolean(this.plano.incluir_nfce),
        incluir_pedidos: Boolean(this.plano.incluir_pedidos),
        incluir_os: this.plano.incluir_os !== false,
        incluir_contratos: Boolean(this.plano.incluir_contratos),
        meta_equipe: this.nullableNumber(this.plano.meta_equipe),
        bonus_equipe_tipo: this.plano.bonus_equipe_tipo || null,
        bonus_equipe_valor: this.plano.bonus_equipe_tipo
          ? Number(this.plano.bonus_equipe_valor || 0)
          : null,
        bonus_equipe_cumulativo: true,
        id_plano_contas: this.plano.id_plano_contas || null,
        vencimento_dias: 0,
        vendedores: this.plano.vendedores.map((item) => ({
          id_vendedor: Number(item.id_vendedor),
          ativo: Boolean(item.ativo),
          comissao_vista: this.nullableNumber(item.comissao_vista),
          comissao_prazo: this.nullableNumber(item.comissao_prazo),
          meta_individual: this.nullableNumber(item.meta_individual),
          tabela_comissao_chave: item.tabela_comissao_chave || null,
          bonus_individual_tipo: null,
          bonus_individual_valor: null,
          participa_meta_equipe: Boolean(item.participa_meta_equipe),
        })),
        faixas: [],
        tabelas_comissao: this.plano.tabelas_comissao.map((tabela) => ({
          chave: tabela._key,
          nome: tabela.nome,
          descricao: tabela.descricao || null,
          vincular_percentuais: tabela.vincular_percentuais !== false,
          ativo: tabela.ativo !== false,
          faixas: tabela.faixas.map((faixa) => ({
            percentual_inicial: Number(faixa.percentual_inicial || 0),
            percentual_final: this.nullableNumber(faixa.percentual_final),
            comissao_percentual: Number(faixa.comissao_percentual || 0),
            comissao_percentual_prazo: Number(
              tabela.vincular_percentuais
                ? faixa.comissao_percentual || 0
                : faixa.comissao_percentual_prazo || 0
            ),
            ativo: faixa.ativo !== false,
          })),
        })),
        formas_pagamento: this.plano.formas_pagamento.map((forma) => ({
          id_forma_pagamento: Number(forma.id_forma_pagamento),
          modalidade: forma.modalidade,
        })),
      };
    },
    validarPlano() {
      if (!this.plano.inicio_vigencia) {
        this.$toast.error("Informe a data inicial da configuração.");
        return false;
      }
      if (
        !this.plano.incluir_nfe &&
        !this.plano.incluir_nfce &&
        !this.plano.incluir_pedidos &&
        !this.plano.incluir_os &&
        !this.plano.incluir_contratos
      ) {
        this.$toast.error("Selecione ao menos uma origem de vendas.");
        return false;
      }

      const nomes = new Set();
      for (const tabela of this.plano.tabelas_comissao) {
        tabela.faixas = this.organizarFaixasTabela(tabela.faixas);
        const nome = String(tabela.nome || "").trim();
        if (!nome) {
          this.$toast.error("Informe o nome de todas as tabelas de comissão.");
          return false;
        }
        if (nomes.has(nome.toLowerCase())) {
          this.$toast.error(
            "Use nomes diferentes para as tabelas de comissão."
          );
          return false;
        }
        nomes.add(nome.toLowerCase());
        if (!tabela.faixas.length) {
          this.$toast.error(`Adicione ao menos uma faixa à tabela “${nome}”.`);
          return false;
        }

        const faixaInvalida = tabela.faixas.find(
          (faixa) =>
            faixa.percentual_final !== null &&
            faixa.percentual_final !== "" &&
            Number(faixa.percentual_final) <= Number(faixa.percentual_inicial)
        );
        if (faixaInvalida) {
          this.$toast.error(
            `Revise os limites das faixas da tabela “${nome}”.`
          );
          return false;
        }
        const percentualInvalido = tabela.faixas.some((faixa) => {
          const vista = Number(faixa.comissao_percentual);
          const prazo = tabela.vincular_percentuais
            ? vista
            : Number(faixa.comissao_percentual_prazo);

          return (
            !Number.isFinite(vista) ||
            vista < 0 ||
            vista > 100 ||
            !Number.isFinite(prazo) ||
            prazo < 0 ||
            prazo > 100
          );
        });
        if (percentualInvalido) {
          this.$toast.error(
            `Informe percentuais entre 0% e 100% em todas as faixas de “${nome}”.`
          );
          return false;
        }
        const semLimiteAntesDaUltima = tabela.faixas.some(
          (faixa, index) =>
            index < tabela.faixas.length - 1 &&
            (faixa.percentual_final === null || faixa.percentual_final === "")
        );
        if (semLimiteAntesDaUltima) {
          this.$toast.error(
            `Somente a última faixa de “${nome}” pode ficar sem limite.`
          );
          return false;
        }
      }

      const vendedorSemMeta = this.plano.vendedores.find(
        (item) =>
          item.tabela_comissao_chave && Number(item.meta_individual || 0) <= 0
      );
      if (vendedorSemMeta) {
        this.$toast.error(
          `Informe a meta individual de ${this.vendedorNome(
            vendedorSemMeta.id_vendedor
          )} para usar a tabela progressiva.`
        );
        return false;
      }
      return true;
    },
    async salvarPlano() {
      if (!this.validarPlano()) return;
      this.saving = true;
      try {
        const response = await this.$http.put(
          "/comissoes/configuracao",
          this.payloadPlano()
        );
        this.plano = this.normalizarPlano(response.data.data);
        await this.simular(true);
        this.$toast.success("Configuração salva e aplicada.");
      } catch (error) {
        this.showError(error, "Não foi possível salvar a configuração.");
      } finally {
        this.saving = false;
      }
    },
    novaChave(prefixo) {
      return (
        prefixo +
        "-" +
        Date.now() +
        "-" +
        Math.random().toString(36).slice(2, 8)
      );
    },
    adicionarTabelaComissao() {
      this.plano.tabelas_comissao.push({
        _key: this.novaChave("tabela"),
        nome: "",
        descricao: "",
        vincular_percentuais: true,
        ativo: true,
        faixas: [
          {
            _key: this.novaChave("faixa"),
            percentual_inicial: 0,
            percentual_final: null,
            comissao_percentual: 0,
            comissao_percentual_prazo: 0,
            ativo: true,
          },
        ],
      });
    },
    removerTabelaComissao(index) {
      const tabela = this.plano.tabelas_comissao[index];
      if (!tabela) return;
      const quantidade = this.quantidadeVendedoresTabela(tabela._key);
      this.plano.vendedores.forEach((vendedor) => {
        if (vendedor.tabela_comissao_chave === tabela._key) {
          vendedor.tabela_comissao_chave = null;
        }
      });
      this.plano.tabelas_comissao.splice(index, 1);
      if (quantidade) {
        this.$toast.info(
          "A tabela foi removida e os vendedores voltaram à comissão padrão."
        );
      }
    },
    quantidadeVendedoresTabela(chave) {
      return this.plano.vendedores.filter(
        (vendedor) => vendedor.tabela_comissao_chave === chave
      ).length;
    },
    alternarVinculoPercentuais(tabela) {
      tabela.vincular_percentuais = !tabela.vincular_percentuais;
      if (tabela.vincular_percentuais) {
        tabela.faixas.forEach((faixa) => {
          faixa.comissao_percentual_prazo = Number(
            faixa.comissao_percentual || 0
          );
        });
      }
    },
    sincronizarPercentualPrazo(tabela, faixa) {
      if (!tabela.vincular_percentuais) return;
      faixa.comissao_percentual_prazo = Number(faixa.comissao_percentual || 0);
    },
    desfocarCampoNumerico(event) {
      if (event && event.currentTarget) {
        event.currentTarget.blur();
      }
    },
    percentualFaixaLabel(faixa, vinculada) {
      const vista = Number(faixa.comissao_percentual || 0);
      const prazo = vinculada
        ? vista
        : Number(faixa.comissao_percentual_prazo || 0);

      return vista === prazo
        ? `${this.percent(vista)} à vista e a prazo`
        : `Vista ${this.percent(vista)} · prazo ${this.percent(prazo)}`;
    },
    adicionarFaixaTabela(tabela) {
      const ultima = tabela.faixas[tabela.faixas.length - 1] || null;
      if (
        ultima &&
        (ultima.percentual_final === null || ultima.percentual_final === "")
      ) {
        this.$toast.warning(
          "Defina o limite da última faixa antes de criar a próxima."
        );
        return;
      }

      const inicio = ultima ? Number(ultima.percentual_final) + 1 : 0;
      tabela.faixas.push({
        _key: this.novaChave("faixa"),
        percentual_inicial: inicio,
        percentual_final: null,
        comissao_percentual: 0,
        comissao_percentual_prazo: 0,
        ativo: true,
      });
    },
    removerFaixaTabela(tabela, index) {
      tabela.faixas.splice(index, 1);
      tabela.faixas = this.organizarFaixasTabela(tabela.faixas);
    },
    onFaixaTabelaInicioAlterado(tabela, faixa) {
      faixa.percentual_inicial = Math.max(
        0,
        Number(faixa.percentual_inicial || 0)
      );
      if (
        faixa.percentual_final !== null &&
        faixa.percentual_final !== "" &&
        Number(faixa.percentual_final) <= Number(faixa.percentual_inicial)
      ) {
        faixa.percentual_final = Number(faixa.percentual_inicial) + 1;
      }
      tabela.faixas = this.organizarFaixasTabela(tabela.faixas);
    },
    onFaixaTabelaLimiteAlterado(tabela, faixa, index) {
      faixa.percentual_final = this.inputNullable(faixa.percentual_final);
      const posterior = tabela.faixas[index + 1] || null;

      if (faixa.percentual_final === null && posterior) {
        faixa.percentual_final = Math.max(
          Number(faixa.percentual_inicial || 0) + 1,
          Number(posterior.percentual_inicial || 0) - 1
        );
        this.$toast.warning("Somente a última faixa pode ficar sem limite.");
      }

      if (
        faixa.percentual_final !== null &&
        Number(faixa.percentual_final) <= Number(faixa.percentual_inicial)
      ) {
        faixa.percentual_final = Number(faixa.percentual_inicial) + 1;
      }
      tabela.faixas = this.organizarFaixasTabela(tabela.faixas);
    },
    organizarFaixasTabela(faixas) {
      let anterior = null;
      return faixas.map((faixa) => {
        if (
          anterior &&
          anterior.percentual_final !== null &&
          anterior.percentual_final !== ""
        ) {
          faixa.percentual_inicial = Number(anterior.percentual_final) + 1;
        }
        anterior = faixa;
        return faixa;
      });
    },
    temMetaIndividual(item) {
      return (
        Boolean(item.tabela_comissao_chave) ||
        Number(item.meta_individual || 0) > 0
      );
    },
    isMoneyBonusType(tipo) {
      return tipo === "fixo";
    },
    aplicarCompetenciaSelecionada() {
      if (!this.competenciaApuracao) return;
      const competencia = moment(
        this.competenciaApuracao + "-01",
        "YYYY-MM-DD"
      );
      this.periodo.data_inicial = competencia
        .clone()
        .startOf("month")
        .format("YYYY-MM-DD");
      this.periodo.data_final = competencia
        .clone()
        .endOf("month")
        .format("YYYY-MM-DD");
    },
    payloadAnalise() {
      const payload = this.periodoPayload();
      if (this.plano.id) {
        payload.id_plano = this.plano.id;
      }

      return payload;
    },
    validarPeriodoAnalise(silencioso = false) {
      if (!this.periodo.data_inicial || !this.periodo.data_final) {
        if (!silencioso) {
          this.$toast.warning("Informe o período completo.");
        }
        return false;
      }
      if (moment(this.periodo.data_inicial).isAfter(this.periodo.data_final)) {
        if (!silencioso) {
          this.$toast.warning(
            "A data inicial não pode ser maior que a data final."
          );
        }
        return false;
      }
      return true;
    },
    async simular(silencioso = false) {
      if (!this.validarPeriodoAnalise(silencioso)) return;

      this.processing = true;
      try {
        const payload = this.payloadAnalise();
        const [previewResponse, historicoResponse] = await Promise.all([
          this.$http.post("/comissoes/apuracoes/preview", payload),
          this.$http.post("/comissoes/apuracoes/historico", {
            ...payload,
            agrupamento: this.agrupamentoHistorico,
          }),
        ]);
        this.resultado = previewResponse.data.data;
        this.historico = historicoResponse.data.data;
      } catch (error) {
        if (!silencioso) {
          this.showError(error, "Não foi possível simular a comissão.");
        }
      } finally {
        this.processing = false;
      }
    },
    async alterarAgrupamentoHistorico(agrupamento) {
      this.agrupamentoHistorico = agrupamento;
      if (!this.validarPeriodoAnalise()) return;

      this.processing = true;
      try {
        const response = await this.$http.post(
          "/comissoes/apuracoes/historico",
          {
            ...this.payloadAnalise(),
            agrupamento,
          }
        );
        this.historico = response.data.data;
      } catch (error) {
        this.showError(
          error,
          "Não foi possível atualizar o histórico de comissões."
        );
      } finally {
        this.processing = false;
      }
    },
    datasPeriodoRapido(tipo) {
      const fim = moment().endOf("month");
      if (tipo === "90_dias") {
        return {
          data_inicial: moment().subtract(89, "days").format("YYYY-MM-DD"),
          data_final: moment().format("YYYY-MM-DD"),
        };
      }
      if (tipo === "6_meses") {
        return {
          data_inicial: fim
            .clone()
            .subtract(5, "months")
            .startOf("month")
            .format("YYYY-MM-DD"),
          data_final: fim.format("YYYY-MM-DD"),
        };
      }
      if (tipo === "12_meses") {
        return {
          data_inicial: fim
            .clone()
            .subtract(11, "months")
            .startOf("month")
            .format("YYYY-MM-DD"),
          data_final: fim.format("YYYY-MM-DD"),
        };
      }

      return {
        data_inicial: moment().startOf("month").format("YYYY-MM-DD"),
        data_final: moment().endOf("month").format("YYYY-MM-DD"),
      };
    },
    async aplicarPeriodoRapido(tipo) {
      const periodo = this.datasPeriodoRapido(tipo);
      this.periodo.data_inicial = periodo.data_inicial;
      this.periodo.data_final = periodo.data_final;
      this.agrupamentoHistorico =
        tipo === "6_meses" || tipo === "12_meses" ? "mes" : "auto";
      await this.simular();
    },
    rotuloPeriodoHistorico(periodo) {
      if (this.historico.agrupamento === "mes") {
        return moment(periodo.periodo).format("MMM/YY");
      }

      return (
        moment(periodo.data_inicial).format("DD/MM") +
        "–" +
        moment(periodo.data_final).format("DD/MM")
      );
    },
    async calcularApuracao() {
      this.aplicarCompetenciaSelecionada();
      const confirmacao = await this.$swal.fire({
        icon: "question",
        title: "Fechar esta competência?",
        text:
          "A competência mensal de " +
          moment(this.periodo.data_inicial).format("MM/YYYY") +
          " será gravada para conferência antes da aprovação.",
        showCancelButton: true,
        confirmButtonText: "Calcular e fechar",
        cancelButtonText: "Cancelar",
      });
      if (!confirmacao.isConfirmed) return;

      this.processing = true;
      try {
        const response = await this.$http.post(
          "/comissoes/apuracoes/calcular",
          this.periodoPayload(false)
        );
        this.definirApuracaoSelecionada(response.data.data);
        this.resultado = null;
        await this.recarregarApuracoes();
        this.$toast.success("Apuração calculada e gravada.");
      } catch (error) {
        this.showError(error, "Não foi possível fechar a apuração.");
      } finally {
        this.processing = false;
      }
    },
    async carregarApuracao(id) {
      this.processing = true;
      try {
        const response = await this.$http.get("/comissoes/apuracoes/" + id);
        this.definirApuracaoSelecionada(response.data.data);
      } catch (error) {
        this.showError(error, "Não foi possível carregar a apuração.");
      } finally {
        this.processing = false;
      }
    },
    async aprovarApuracao() {
      const confirmacao = await this.$swal.fire({
        icon: "warning",
        title: "Aprovar a apuração?",
        text: "Após a aprovação, valores e lançamentos ficarão protegidos contra alteração.",
        showCancelButton: true,
        confirmButtonText: "Aprovar",
        cancelButtonText: "Cancelar",
        confirmButtonColor: this.primaryBrandColor,
      });
      if (!confirmacao.isConfirmed) return;

      this.processing = true;
      try {
        const response = await this.$http.post(
          "/comissoes/apuracoes/" + this.apuracaoSelecionada.id + "/aprovar"
        );
        this.definirApuracaoSelecionada(response.data.data, false);
        await this.recarregarApuracoes();
        this.$toast.success("Apuração aprovada.");
      } catch (error) {
        this.showError(error, "Não foi possível aprovar a apuração.");
      } finally {
        this.processing = false;
      }
    },
    async cancelarApuracao() {
      const confirmacao = await this.$swal.fire({
        icon: "warning",
        title: "Cancelar esta apuração?",
        text: "A apuração será excluída e a competência poderá ser calculada novamente.",
        showCancelButton: true,
        confirmButtonText: "Cancelar apuração",
        cancelButtonText: "Voltar",
        confirmButtonColor: "#f64e60",
      });
      if (!confirmacao.isConfirmed) return;

      this.processing = true;
      try {
        await this.$http.post(
          "/comissoes/apuracoes/" + this.apuracaoSelecionada.id + "/cancelar"
        );
        this.apuracaoSelecionada = null;
        await this.recarregarApuracoes();
        this.$toast.success(
          "Apuração removida. A competência já pode ser calculada novamente."
        );
      } catch (error) {
        this.showError(error, "Não foi possível cancelar a apuração.");
      } finally {
        this.processing = false;
      }
    },
    async gerarFinanceiro() {
      const condicionado = this.apuracaoPorRecebimento;
      const confirmacao = await this.$swal.fire({
        icon: "question",
        title: condicionado
          ? "Gerar a comissão a pagar deste mês?"
          : "Gerar contas a pagar?",
        text: condicionado
          ? "O financeiro usará somente as baixas desta competência. Recebimentos de vendas antigas entram no pagamento, sem voltar para a meta ou para a comissão gerada do mês."
          : "Será criada uma conta por vendedor. Repetir esta operação não criará duplicidades.",
        input: "text",
        inputLabel: "Data de vencimento",
        inputValue: moment().format("YYYY-MM-DD"),
        inputAttributes: {
          required: true,
        },
        onOpen: (modal) => {
          const input = modal.querySelector(".swal2-input");
          if (input) input.type = "date";
        },
        showCancelButton: true,
        confirmButtonText: condicionado
          ? "Gerar valor a pagar"
          : "Gerar financeiro",
        cancelButtonText: "Cancelar",
        preConfirm: (dataVencimento) => {
          if (!dataVencimento) {
            this.$swal.showValidationMessage("Informe a data de vencimento.");
            return false;
          }

          return dataVencimento;
        },
      });
      if (!confirmacao.isConfirmed) return;

      this.processing = true;
      try {
        const response = await this.$http.post(
          "/comissoes/apuracoes/" +
            this.apuracaoSelecionada.id +
            "/gerar-financeiro",
          { data_vencimento: confirmacao.value }
        );
        this.definirApuracaoSelecionada(response.data.data, false);
        await this.recarregarApuracoes();
        this.$toast.success(
          condicionado
            ? "Comissão a pagar enviada ao financeiro sem duplicidades."
            : "Financeiro gerado sem duplicidades."
        );
      } catch (error) {
        this.showError(error, "Não foi possível gerar o financeiro.");
      } finally {
        this.processing = false;
      }
    },
    definirApuracaoSelecionada(apuracao, expandirPrimeiro = true) {
      this.apuracaoSelecionada = apuracao;
      if (
        expandirPrimeiro &&
        apuracao &&
        apuracao.vendedores &&
        apuracao.vendedores.length
      ) {
        this.sellerDetailsOpen = {
          [apuracao.vendedores[0].id]: true,
        };
      }
    },
    vendedorSnapshot(vendedor) {
      if (!vendedor) return {};
      if (vendedor.calculo_snapshot) return vendedor.calculo_snapshot;

      return {
        tabela_comissao: vendedor.tabela_comissao || null,
        faixa_comissao: vendedor.faixa_comissao || null,
      };
    },
    vendedorFuture(vendedor) {
      const vazio = {
        habilitado: false,
        quantidade: 0,
        valor_base: 0,
        comissao_estimada: null,
        estimativa_disponivel: false,
        proximo_vencimento: null,
        itens: [],
      };
      if (!vendedor) return vazio;

      return (
        vendedor.comissoes_futuras ||
        this.vendedorSnapshot(vendedor).comissoes_futuras ||
        vazio
      );
    },
    vendedorPayment(vendedor) {
      const vazio = {
        condicionado_recebimento: false,
        valor_gerado: Number((vendedor && vendedor.total_pagar) || 0),
        valor_a_pagar: 0,
        valor_liberado: 0,
        saldo_liberar: 0,
        quantidade: 0,
        itens: [],
      };
      if (!vendedor) return vazio;

      return {
        ...vazio,
        ...(vendedor.comissao_a_pagar || {}),
      };
    },
    vendedorFinance(vendedor) {
      const vazio = {
        condicionado_recebimento: false,
        valor_calculado: Number((vendedor && vendedor.total_pagar) || 0),
        valor_gerado: Number((vendedor && vendedor.total_pagar) || 0),
        valor_a_pagar: 0,
        valor_disponivel: 0,
        valor_aguardando_recebimento: 0,
        valor_liberado: 0,
        saldo_liberar: 0,
        saldo_total: Number((vendedor && vendedor.total_pagar) || 0),
      };
      if (!vendedor) return vazio;

      return {
        ...vazio,
        ...(vendedor.resumo_financeiro ||
          this.vendedorSnapshot(vendedor).resumo_financeiro ||
          {}),
      };
    },
    monthYear(value) {
      return value ? moment(value).format("MM/YYYY") : "—";
    },
    sellerFinanceStatusLabel(vendedor) {
      const labels = {
        gerado: "Financeiro concluído",
        parcial: "Liberação parcial",
        aguardando_recebimento: "Aguardando recebimento",
        pendente: "Financeiro pendente",
      };

      return labels[vendedor.status_financeiro] || "Financeiro pendente";
    },
    sellerFinanceStatusClass(vendedor) {
      const classes = {
        gerado: "label-light-success",
        parcial: "label-light-info",
        aguardando_recebimento: "label-light-warning",
        pendente: "label-light-primary",
      };

      return classes[vendedor.status_financeiro] || "label-light";
    },
    sellerAccountLabels(vendedor) {
      return (vendedor.liberacoes || [])
        .filter((item) => item.id_conta_pagar)
        .map((item) => "#" + item.id_conta_pagar)
        .join(", ");
    },
    vendedorRegraLabel(vendedor) {
      const tabela = this.vendedorSnapshot(vendedor).tabela_comissao;
      return tabela && tabela.nome
        ? tabela.nome
        : "Percentuais à vista e a prazo";
    },
    vendedorFaixaLabel(vendedor) {
      const snapshot = this.vendedorSnapshot(vendedor);
      const faixa = snapshot.faixa_comissao;
      if (!faixa) {
        return snapshot.tabela_comissao
          ? "Abaixo da primeira faixa"
          : "Regra padrão";
      }

      const limiteFinal =
        faixa.percentual_final === null || faixa.percentual_final === undefined
          ? "sem limite"
          : this.percent(faixa.percentual_final);
      return (
        this.percent(faixa.percentual_inicial) +
        " a " +
        limiteFinal +
        " · " +
        this.percentuaisFaixaSnapshot(faixa)
      );
    },
    vendedorPercentualLabel(vendedor) {
      const faixa = this.vendedorSnapshot(vendedor).faixa_comissao;
      if (faixa) return this.percentuaisFaixaSnapshot(faixa);

      return (
        "Vista " +
        this.percent(vendedor.comissao_vista_percentual) +
        " · prazo " +
        this.percent(vendedor.comissao_prazo_percentual)
      );
    },
    percentuaisFaixaSnapshot(faixa) {
      const vista = Number(
        faixa.comissao_percentual_vista ?? faixa.comissao_percentual ?? 0
      );
      const prazo = Number(faixa.comissao_percentual_prazo ?? vista);

      return vista === prazo
        ? this.percent(vista)
        : `Vista ${this.percent(vista)} · prazo ${this.percent(prazo)}`;
    },
    progressWidth(valor) {
      return Math.max(0, Math.min(100, Number(valor || 0))) + "%";
    },
    initials(nome) {
      return String(nome || "?")
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((parte) => parte.charAt(0).toUpperCase())
        .join("");
    },
    toggleSellerDetails(id) {
      this.$set(this.sellerDetailsOpen, id, !this.sellerDetailsVisible(id));
    },
    sellerDetailsVisible(id) {
      return Boolean(this.sellerDetailsOpen[id]);
    },
    origemLabel(origem) {
      const labels = {
        nfe: "NF-e",
        nfce: "NFC-e",
        pedido: "Pedido",
        os: "OS",
        contrato: "Contrato",
      };
      return labels[origem] || origem || "Ajuste";
    },
    modalidadeLabel(modalidade) {
      const labels = {
        vista: "À vista",
        prazo: "A prazo",
      };
      return labels[modalidade] || modalidade || "—";
    },
    async abrirPdfVendedor(vendedor) {
      const janela = window.open("", "_blank");
      this.pdfSellerId = vendedor.id_vendedor;
      try {
        const response = await this.$http.get(
          "/comissoes/apuracoes/" +
            this.apuracaoSelecionada.id +
            "/vendedores/" +
            vendedor.id_vendedor +
            "/pdf",
          { responseType: "blob" }
        );
        const arquivo = new Blob([response.data], {
          type: "application/pdf",
        });
        const url = window.URL.createObjectURL(arquivo);
        if (janela) {
          janela.location.href = url;
        } else {
          const link = document.createElement("a");
          link.href = url;
          link.download =
            "apuracao-" +
            this.apuracaoSelecionada.id +
            "-" +
            vendedor.vendedor_nome +
            ".pdf";
          link.click();
        }
        window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      } catch (error) {
        if (janela) janela.close();
        this.showError(error, "Não foi possível gerar o PDF da apuração.");
      } finally {
        this.pdfSellerId = null;
      }
    },
    periodoPayload(comFiltroVendedor = true) {
      const payload = {
        data_inicial: this.periodo.data_inicial,
        data_final: this.periodo.data_final,
      };
      if (comFiltroVendedor && this.periodo.id_vendedor) {
        payload.id_vendedor = this.periodo.id_vendedor;
      }
      return payload;
    },
    async recarregarApuracoes() {
      const response = await this.$http.get("/comissoes/apuracoes");
      this.apuracoes = response.data.data || [];
    },
    vendedorNome(id) {
      const vendedor = this.vendedores.find(
        (item) => Number(item.id_vendedor) === Number(id)
      );
      return vendedor ? vendedor.nome : "Vendedor #" + id;
    },
    inferirModalidade(descricao) {
      const texto = String(descricao || "").toLowerCase();
      if (/nenhum|cortesia|sem pagamento/.test(texto)) return "ignorar";
      if (/vista|pix|dinheiro|débito|debito/.test(texto)) return "vista";
      return "prazo";
    },
    inputNullable(valor) {
      return valor === null || valor === undefined || valor === ""
        ? null
        : Number(valor);
    },
    nullableNumber(valor) {
      return valor === null || valor === undefined || valor === ""
        ? null
        : Number(valor);
    },
    bonusValueLabel(tipo) {
      if (tipo === "fixo") return "Valor por vendedor (R$)";
      if (tipo === "percentual_comissao") return "Percentual sobre a comissão";
      if (tipo === "pontos_percentuais") return "Pontos sobre as vendas";
      return "Valor do bônus";
    },
    statusLabel(status) {
      const labels = {
        rascunho: "Rascunho",
        ativo: "Ativo",
        inativo: "Encerrado",
        calculado: "Calculado",
        aprovado: "Aprovado",
        financeiro_gerado: "Financeiro gerado",
        pago: "Pago",
        cancelado: "Cancelado",
      };
      return labels[status] || status || "";
    },
    statusClass(status) {
      const classes = {
        rascunho: "label-light-warning",
        ativo: "label-light-success",
        inativo: "label-light-dark",
        calculado: "label-light-info",
        aprovado: "label-light-primary",
        financeiro_gerado: "label-light-success",
        pago: "label-light-success",
        cancelado: "label-light-danger",
      };
      return classes[status] || "label-light";
    },
    money(valor) {
      return Number(valor || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    moneyCompact(valor) {
      return Number(valor || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
        notation: "compact",
        maximumFractionDigits: 1,
      });
    },
    moneyNullable(valor) {
      return valor === null || valor === undefined
        ? "Sem meta"
        : this.money(valor);
    },
    percent(valor) {
      return (
        Number(valor || 0).toLocaleString("pt-BR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }) + "%"
      );
    },
    dateBr(data) {
      return data ? moment(data).format("DD/MM/YYYY") : "-";
    },
    showError(error, fallback) {
      const response = error && error.response ? error.response.data : null;
      const errors = response && response.errors ? response.errors : null;
      const firstError = errors
        ? Object.values(errors).reduce((all, item) => all.concat(item), [])[0]
        : null;
      this.$toast.error(
        firstError ||
          (response && (response.message || response.msg)) ||
          fallback
      );
    },
  },
  watch: {
    async tabIndex(value) {
      if (value !== 5) return;
      this.aplicarCompetenciaSelecionada();
      await this.simular(true);
    },
  },
  async mounted() {
    if (this.itsblocked) {
      this.$router.push({ name: "assinatura" });
      return;
    }
    await this.carregarTudo();
    await this.simular(true);
  },
};
</script>

<style scoped>
.commission-page {
  max-width: 100%;
  padding-bottom: 24px;
}

.commission-page .btn-primary {
  background-color: var(--primary-color) !important;
  border-color: var(--primary-color) !important;
}

.commission-page .btn-light-primary {
  background: color-mix(in srgb, var(--primary-color) 12%, #fff) !important;
  border-color: transparent !important;
  color: var(--primary-color) !important;
}

.commission-page .label-light-primary {
  background: color-mix(in srgb, var(--primary-color) 12%, #fff) !important;
  color: var(--primary-color) !important;
}

.analysis-filter {
  background: linear-gradient(135deg, #f9fafc 0%, #f3f6f9 100%);
  border: 1px solid #eef0f6;
  border-radius: 0.75rem;
  padding: 1.25rem;
}

.plan-navigation {
  align-items: center;
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  min-height: 38px;
}

.plan-navigation-summary {
  align-items: center;
  display: flex;
  gap: 0.65rem;
}

.configuration-icon {
  align-items: center;
  background: color-mix(in srgb, var(--primary-color) 12%, #fff);
  border-radius: 9px;
  color: var(--primary-color);
  display: flex;
  flex: 0 0 36px;
  height: 36px;
  justify-content: center;
  width: 36px;
}

.commission-toolbar {
  gap: 0.25rem;
}

.plan-version-select {
  min-width: 270px;
  max-width: 340px;
}

.commission-tabs ::v-deep .nav-pills {
  border-bottom: 1px solid #ebedf3;
  gap: 0.4rem;
  padding-bottom: 1rem;
  margin-bottom: 1.25rem;
}

.commission-tabs ::v-deep .nav-link {
  color: #7e8299;
  font-weight: 600;
}

.commission-tabs ::v-deep .nav-link.active {
  background: var(--primary-color);
  color: #fff;
}

.overview-kpi {
  align-items: center;
  background: #fff;
  border: 1px solid #eef0f6;
  border-radius: 0.75rem;
  display: flex;
  gap: 1rem;
  padding: 1.25rem;
  transition: box-shadow 0.15s ease, transform 0.15s ease;
}

.overview-kpi:hover {
  box-shadow: 0 8px 24px rgba(31, 45, 61, 0.08);
  transform: translateY(-2px);
}

.overview-kpi-icon {
  align-items: center;
  border-radius: 12px;
  display: flex;
  flex: 0 0 48px;
  font-size: 1.35rem;
  height: 48px;
  justify-content: center;
  width: 48px;
}

.overview-kpi span,
.overview-kpi small {
  color: #7e8299;
  display: block;
}

.overview-kpi strong {
  color: #181c32;
  display: block;
  font-size: 1.35rem;
  line-height: 1.7;
  white-space: nowrap;
}

.dashboard-card {
  background: #fff;
  border: 1px solid #eef0f6;
  border-radius: 0.75rem;
  overflow: hidden;
}

.dashboard-card-header {
  align-items: center;
  border-bottom: 1px solid #eef0f6;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  padding: 1.35rem 1.5rem;
}

.dashboard-card-header h3 {
  color: #3f4254;
  font-size: 1.05rem;
  font-weight: 700;
  margin: 0 0 0.2rem;
}

.dashboard-card-header p {
  color: #7e8299;
  margin: 0;
}

.dashboard-card-body {
  min-height: 300px;
  padding: 0.75rem 1rem 0.5rem;
  position: relative;
}

.history-chart {
  min-height: 350px;
}

.history-grouping,
.quick-periods {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
}

.quick-periods {
  border-top: 1px solid #e4e6ef;
  color: #7e8299;
  margin-top: 1rem;
  padding-top: 0.85rem;
}

.quick-periods > span {
  font-size: 0.82rem;
  font-weight: 600;
  margin-right: 0.25rem;
}

.history-grouping .btn,
.quick-periods .btn {
  border-radius: 999px;
  font-weight: 600;
}

.dashboard-chip {
  background: color-mix(in srgb, var(--primary-color) 12%, #fff);
  border-radius: 999px;
  color: var(--primary-color);
  display: inline-block;
  font-size: 0.78rem;
  font-weight: 700;
  padding: 0.4rem 0.75rem;
  white-space: nowrap;
}

.chart-empty {
  align-items: center;
  background: rgba(255, 255, 255, 0.92);
  color: #7e8299;
  display: flex;
  inset: 0;
  justify-content: center;
  padding: 2rem;
  position: absolute;
  text-align: center;
}

.ranking-table thead th {
  background: #f9fafc;
  border-bottom: 1px solid #eef0f6;
  color: #7e8299;
  font-size: 0.78rem;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.ranking-table td,
.ranking-table th {
  padding: 1rem 1.5rem;
}

.ranking-position {
  align-items: center;
  background: #f3f6f9;
  border-radius: 8px;
  color: #7e8299;
  display: inline-flex;
  font-weight: 700;
  height: 32px;
  justify-content: center;
  width: 32px;
}

.ranking-table tbody tr:first-child .ranking-position {
  background: #fff4de;
  color: #ffa800;
}

.progress-column {
  min-width: 235px;
}

.empty-overview {
  border: 1px dashed #c8c9d1;
  border-radius: 0.75rem;
  color: #7e8299;
  padding: 5rem 2rem;
  text-align: center;
}

.empty-overview h4 {
  color: #3f4254;
  font-weight: 700;
  margin-top: 1rem;
}

.section-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.section-heading h5 {
  margin-bottom: 0.25rem;
  font-weight: 700;
  color: #3f4254;
}

.section-heading p {
  margin: 0;
  color: #7e8299;
}

.tab-intro {
  border-bottom: 1px solid #eef0f6;
  margin-bottom: 1.25rem;
  padding-bottom: 1.1rem;
}

.section-eyebrow {
  color: var(--primary-color);
  display: block;
  font-size: 0.72rem;
  font-weight: 750;
  letter-spacing: 0.07em;
  margin-bottom: 0.3rem;
  text-transform: uppercase;
}

.config-panel {
  background: #fff;
  border: 1px solid #e8ebf1;
  border-radius: 0.85rem;
  box-shadow: 0 3px 12px rgba(31, 45, 61, 0.035);
  margin-bottom: 1rem;
  padding: 1.25rem 1.35rem 0.25rem;
}

.monthly-policy-note {
  align-items: center;
  background: #f3f9ff;
  border: 1px solid #d9ecff;
  border-radius: 0.75rem;
  color: #3699ff;
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  padding: 0.8rem 1rem;
}

.monthly-policy-note > svg {
  flex: 0 0 auto;
  font-size: 1.1rem;
}

.monthly-policy-note strong,
.monthly-policy-note span {
  display: block;
}

.monthly-policy-note strong {
  color: #3f4254;
  margin-bottom: 0.1rem;
}

.monthly-policy-note span {
  color: #7e8299;
  font-size: 0.84rem;
}

.config-panel-heading,
.settlement-period-heading {
  align-items: center;
  display: flex;
  gap: 0.85rem;
  margin-bottom: 1.1rem;
}

.config-panel-heading h6,
.settlement-period-heading h6 {
  color: #3f4254;
  font-size: 1rem;
  font-weight: 700;
  margin: 0 0 0.15rem;
}

.config-panel-heading p,
.settlement-period-heading p {
  color: #7e8299;
  margin: 0;
}

.config-panel-icon {
  align-items: center;
  background: #e1f0ff;
  border-radius: 10px;
  color: #3699ff;
  display: flex;
  flex: 0 0 40px;
  font-size: 1.05rem;
  height: 40px;
  justify-content: center;
  width: 40px;
}

.config-panel-icon.is-coral {
  background: color-mix(in srgb, var(--primary-color) 12%, #fff);
  color: var(--primary-color);
}

.config-panel-icon.is-green {
  background: #e8fff3;
  color: #0bb783;
}

.source-options {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1.5rem;
  min-height: 38px;
  align-items: center;
}

.timing-setting {
  align-items: center;
  border-top: 1px solid #eef0f6;
  display: grid;
  gap: 1rem;
  grid-template-columns: minmax(180px, 0.7fr) minmax(420px, 1.3fr);
  margin-top: 0.25rem;
  padding: 1rem 0;
}

.timing-setting-copy span {
  color: #3f4254;
  display: block;
  font-weight: 700;
}

.timing-setting-copy p {
  color: #7e8299;
  font-size: 0.84rem;
  margin: 0.2rem 0 0;
}

.timing-options {
  display: grid;
  gap: 0.65rem;
  grid-template-columns: repeat(2, 1fr);
}

.timing-option {
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e8ebf1;
  border-radius: 0.7rem;
  color: #7e8299;
  display: flex;
  gap: 0.75rem;
  min-height: 62px;
  padding: 0.75rem 0.9rem;
  text-align: left;
  transition: all 0.15s ease;
}

.timing-option > svg {
  font-size: 1.2rem;
}

.timing-option span,
.timing-option strong,
.timing-option small {
  display: block;
}

.timing-option strong {
  color: #3f4254;
}

.timing-option.is-selected {
  background: color-mix(in srgb, var(--primary-color) 8%, #fff);
  border-color: color-mix(in srgb, var(--primary-color) 55%, #fff);
  box-shadow: inset 3px 0 0 var(--primary-color);
  color: var(--primary-color);
}

.inheritance-note {
  align-items: center;
  background: #f1f7ff;
  border: 1px solid #dceaff;
  border-radius: 0.7rem;
  color: #486581;
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  padding: 0.85rem 1rem;
}

.inheritance-note > svg {
  color: #3699ff;
  flex: 0 0 auto;
}

.competence-card {
  align-items: flex-start;
  background: linear-gradient(135deg, #f9fafc 0%, #fff 100%);
  border: 1px solid #e4e6ef;
  border-radius: 0.75rem;
  display: flex;
  gap: 1.25rem;
  padding: 1.35rem;
}

.competence-card-icon {
  align-items: center;
  background: #e1f0ff;
  border-radius: 12px;
  color: #3699ff;
  display: flex;
  flex: 0 0 48px;
  font-size: 1.35rem;
  height: 48px;
  justify-content: center;
  width: 48px;
}

.competence-card-content {
  flex: 1;
}

.competence-card h5 {
  color: #3f4254;
  font-weight: 700;
  margin-bottom: 0.25rem;
}

.competence-card p {
  color: #7e8299;
  margin: 0;
}

.competence-explanation {
  border-radius: 0.55rem;
  display: flex;
  margin-top: 1rem;
  padding: 0.8rem 1rem;
}

.competence-explanation.is-receipt {
  background: #e8fff3;
  color: #0bb783;
}

.competence-explanation.is-sale {
  background: #e1f0ff;
  color: #3699ff;
}

.table th {
  white-space: nowrap;
}

.table td {
  vertical-align: middle;
}

.money-column {
  min-width: 145px;
}

.rule-row {
  background: #f9f9fb;
  border: 1px solid #ebedf3;
  border-radius: 0.6rem;
  padding: 1rem 1rem 0;
  margin-bottom: 1rem;
}

.commission-table-column {
  min-width: 235px;
}

.commission-inherited {
  opacity: 0.72;
}

.commission-table-card {
  border: 1px solid #e5e9f0;
  border-radius: 0.85rem;
  margin-bottom: 1rem;
  overflow: hidden;
}

.commission-table-header {
  align-items: center;
  background: linear-gradient(135deg, #fbfcfe 0%, #fff 100%);
  border-bottom: 1px solid #edf0f5;
  display: flex;
  gap: 1rem;
  padding: 1.15rem 1.25rem;
}

.commission-table-identity {
  display: grid;
  flex: 1;
  gap: 0.75rem;
  grid-template-columns: minmax(260px, 0.8fr) minmax(280px, 1.2fr);
}

.commission-table-usage {
  align-items: center;
  display: flex;
  gap: 0.65rem;
}

.commission-tier-heading {
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding: 1rem 1.25rem 0.75rem;
}

.commission-tier-heading strong,
.commission-tier-heading small {
  display: block;
}

.commission-tier-heading small {
  color: #7e8299;
  margin-top: 0.15rem;
}

.commission-tier-actions {
  align-items: center;
  display: flex;
  gap: 0.65rem;
}

.commission-link-button {
  white-space: nowrap;
}

.commission-tier-row {
  align-items: start;
  border-top: 1px solid #f0f1f5;
  display: grid;
  gap: 0.85rem;
  grid-template-columns:
    32px minmax(112px, 0.62fr) minmax(112px, 0.62fr)
    minmax(130px, 0.72fr) minmax(130px, 0.72fr)
    minmax(220px, 1.25fr) 38px;
  padding: 1rem 1.25rem;
}

.commission-tier-row .form-group {
  margin-bottom: 0;
}

.tier-sequence {
  align-items: center;
  background: #f3f6f9;
  border-radius: 8px;
  color: #7e8299;
  display: flex;
  font-weight: 700;
  height: 32px;
  justify-content: center;
  margin-top: 1.75rem;
  width: 32px;
}

.tier-result {
  background: #eefbf6;
  border-radius: 0.55rem;
  margin-top: 1.7rem;
  padding: 0.65rem 0.8rem;
}

.tier-result span,
.tier-result strong {
  display: block;
}

.tier-result span {
  color: #7e8299;
  font-size: 0.72rem;
}

.tier-result strong {
  color: #0b8d68;
  font-size: 0.82rem;
}

.commission-tier-row > .btn {
  margin-top: 1.75rem;
}

.tier-empty {
  color: #7e8299;
  padding: 0.5rem 1.25rem 1.25rem;
  text-align: center;
}

.range-number {
  align-items: center;
  background: color-mix(in srgb, var(--primary-color) 12%, #fff);
  border-radius: 10px;
  color: var(--primary-color);
  display: flex;
  flex: 0 0 38px;
  font-weight: 750;
  height: 38px;
  justify-content: center;
  width: 38px;
}

.range-context {
  display: flex;
  gap: 0.55rem;
  margin-left: auto;
}

.range-context .custom-select {
  min-width: 150px;
}

.range-helper {
  color: #7e8299;
  display: block;
  font-size: 0.75rem;
  margin-top: 0.35rem;
}

.range-helper-success {
  color: #0bb783;
  font-weight: 600;
}

.empty-state {
  border: 1px dashed #c8c9d1;
  border-radius: 0.6rem;
  color: #7e8299;
  padding: 2rem;
  text-align: center;
}

.payment-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
}

.payment-item {
  display: grid;
  grid-template-columns: 1fr 150px;
  align-items: center;
  gap: 1rem;
  border: 1px solid #ebedf3;
  border-radius: 0.6rem;
  padding: 1rem;
}

.adjustment-box {
  background: #f9f9fb;
  border-radius: 0.6rem;
  padding: 1rem 1rem 0;
}

.settlement-actions {
  align-items: center;
  background: #f9fafc;
  border: 1px solid #eef0f6;
  border-radius: 0.7rem;
  display: flex;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  padding: 1rem 1.25rem;
}

.settlement-period-card {
  background: #f8fafc;
  border: 1px solid #e5e9f0;
  border-radius: 0.85rem;
  margin-bottom: 1.5rem;
  padding: 1.25rem;
}

.settlement-period-actions {
  border-top: 1px solid #e5e9f0;
  margin-top: 1rem;
  padding-top: 1rem;
  text-align: right;
}

.settlement-draft-note {
  background: #fff8e7;
  border-radius: 0.55rem;
  color: #9a6b00;
  font-size: 0.84rem;
  margin-top: 0.85rem;
  padding: 0.7rem 0.85rem;
}

.result-panel,
.settlement-detail {
  border: 1px solid #ebedf3;
  border-radius: 0.7rem;
  padding: 1.5rem;
}

.settlement-detail-v2 {
  background: #f8fafc;
  border-color: #e4e8f0;
}

.settlement-detail-header {
  align-items: center;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  margin-bottom: 1.25rem;
}

.settlement-detail-header h5 {
  color: #20243a;
  font-weight: 750;
}

.settlement-detail-header p {
  color: #7e8299;
  margin: 0.35rem 0 0;
}

.settlement-header-actions {
  display: flex;
  gap: 0.65rem;
}

.settlement-overview-grid {
  display: grid;
  gap: 0.85rem;
  grid-template-columns: repeat(4, minmax(145px, 1fr));
}

.settlement-overview-item {
  background: #fff;
  border: 1px solid #e7eaf1;
  border-radius: 0.7rem;
  padding: 1rem 1.1rem;
}

.settlement-overview-item span {
  color: #7e8299;
  display: block;
  font-size: 0.78rem;
  margin-bottom: 0.35rem;
}

.settlement-overview-item strong {
  color: #30344b;
  display: block;
  font-size: 1.08rem;
}

.settlement-overview-item small {
  color: #9a9fb0;
  display: block;
  font-size: 0.7rem;
  line-height: 1.35;
  margin-top: 0.35rem;
}

.settlement-overview-item.is-total {
  background: linear-gradient(
    135deg,
    color-mix(in srgb, var(--primary-color) 10%, #fff),
    #fff
  );
  border-color: color-mix(in srgb, var(--primary-color) 32%, #fff);
}

.settlement-overview-item.is-total strong {
  color: var(--primary-color);
}

.future-commission-banner {
  align-items: center;
  background: #fff9e8;
  border: 1px solid #f2dda0;
  border-radius: 0.75rem;
  color: #755d18;
  display: flex;
  gap: 0.85rem;
  margin-top: 1rem;
  padding: 0.9rem 1rem;
}

.future-commission-banner strong {
  color: #6b530b;
  display: block;
}

.future-commission-banner p {
  margin: 0.15rem 0 0;
}

.future-commission-icon {
  align-items: center;
  background: #ffedb7;
  border-radius: 0.65rem;
  color: #a97b00;
  display: flex;
  flex: 0 0 42px;
  font-size: 1.1rem;
  height: 42px;
  justify-content: center;
}

.future-commission-badge {
  background: #ffedb7;
  border-radius: 999px;
  color: #8b6700;
  font-size: 0.74rem;
  font-weight: 700;
  margin-left: auto;
  padding: 0.35rem 0.65rem;
  white-space: nowrap;
}

.adjustment-title {
  display: flex;
  flex-direction: column;
  margin-bottom: 0.85rem;
}

.adjustment-title span {
  color: #7e8299;
  font-size: 0.8rem;
  margin-top: 0.15rem;
}

.seller-settlement-list {
  display: grid;
  gap: 1rem;
  margin-top: 1.25rem;
}

.seller-settlement-card {
  background: #fff;
  border: 1px solid #e4e8f0;
  border-radius: 0.85rem;
  box-shadow: 0 4px 16px rgba(31, 36, 58, 0.035);
  overflow: hidden;
}

.seller-settlement-header {
  align-items: center;
  border-bottom: 1px solid #edf0f5;
  display: flex;
  gap: 0.8rem;
  padding: 1rem 1.15rem;
}

.seller-settlement-header h6 {
  color: #24283d;
  font-size: 1rem;
  font-weight: 750;
  margin: 0;
}

.seller-settlement-header p {
  color: #7e8299;
  font-size: 0.78rem;
  margin: 0.15rem 0 0;
}

.seller-avatar {
  align-items: center;
  background: color-mix(in srgb, var(--primary-color) 12%, #fff);
  border-radius: 0.65rem;
  color: var(--primary-color);
  display: flex;
  flex: 0 0 42px;
  font-weight: 750;
  height: 42px;
  justify-content: center;
}

.seller-settlement-tags {
  align-items: center;
  display: flex;
  gap: 0.55rem;
  margin-left: auto;
}

.seller-rule-chip {
  background: #eef6ff;
  border-radius: 999px;
  color: #3577b9;
  font-size: 0.74rem;
  font-weight: 650;
  padding: 0.35rem 0.6rem;
}

.seller-settlement-grid {
  display: grid;
  grid-template-columns: minmax(220px, 1.15fr) minmax(230px, 1.2fr) minmax(
      195px,
      0.8fr
    );
}

.seller-performance-panel,
.seller-commission-panel,
.seller-finance-panel {
  padding: 1.1rem 1.15rem;
}

.seller-commission-panel,
.seller-finance-panel {
  border-left: 1px solid #edf0f5;
}

.seller-panel-label {
  color: #8b90a2;
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  margin-bottom: 0.65rem;
  text-transform: uppercase;
}

.seller-primary-value {
  color: #282c42;
  font-size: 1.35rem;
  font-weight: 750;
}

.seller-goal-row {
  color: #777d91;
  display: flex;
  font-size: 0.78rem;
  justify-content: space-between;
  margin-top: 0.7rem;
}

.seller-goal-row strong {
  color: #41465d;
}

.seller-progress {
  background: #edf0f5;
  border-radius: 999px;
  height: 7px;
  margin-top: 0.4rem;
  overflow: hidden;
}

.seller-progress span {
  background: linear-gradient(90deg, var(--primary-color), #1bc5bd);
  border-radius: inherit;
  display: block;
  height: 100%;
}

.seller-rule-result {
  align-items: center;
  background: #f6f8fb;
  border-radius: 0.55rem;
  display: flex;
  justify-content: space-between;
  margin-top: 0.8rem;
  padding: 0.55rem 0.65rem;
}

.seller-rule-result span {
  color: #7e8299;
  font-size: 0.75rem;
}

.seller-rule-result strong {
  color: #3577b9;
  font-size: 0.8rem;
}

.commission-breakdown-row,
.commission-breakdown-total {
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding: 0.28rem 0;
}

.commission-breakdown-row span {
  color: #73798d;
  font-size: 0.78rem;
}

.commission-breakdown-row strong {
  color: #363a50;
  font-size: 0.82rem;
}

.commission-breakdown-total {
  border-top: 1px solid #e7eaf0;
  color: var(--primary-color);
  margin-top: 0.35rem;
  padding-top: 0.6rem;
}

.commission-breakdown-total strong {
  font-size: 1rem;
}

.seller-finance-panel {
  text-align: center;
}

.seller-finance-panel > strong {
  color: #35394f;
  display: block;
}

.seller-finance-panel > p {
  color: #858a9c;
  font-size: 0.75rem;
  margin: 0.15rem 0 0.65rem;
}

.finance-status-icon {
  color: #1bc5bd;
  font-size: 1.4rem;
  line-height: 1;
  margin-bottom: 0.35rem;
}

.seller-future-strip {
  align-items: center;
  background: #fff9e9;
  border-top: 1px solid #f4e5b9;
  color: #79601a;
  display: flex;
  gap: 0.75rem;
  padding: 0.75rem 1.15rem;
}

.seller-future-strip > svg {
  color: #b4870a;
  flex: 0 0 auto;
}

.seller-future-strip strong,
.seller-future-strip span {
  display: block;
}

.seller-future-strip span {
  font-size: 0.76rem;
  margin-top: 0.1rem;
}

.seller-ledger {
  border-top: 1px solid #e8ebf1;
}

.seller-ledger-heading {
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding: 0.85rem 1.15rem 0.55rem;
}

.seller-ledger-heading strong,
.seller-ledger-heading span {
  display: block;
}

.seller-ledger-heading span {
  color: #858a9b;
  font-size: 0.76rem;
  margin-top: 0.12rem;
}

.seller-ledger .table-responsive {
  padding: 0 1.15rem 1rem;
}

.payment-ledger {
  background: linear-gradient(180deg, #f4f9ff 0%, #fff 100%);
  border-bottom: 1px solid #dfeaf6;
}

.payment-ledger-total {
  background: #e3f1ff;
  border-radius: 999px;
  color: #266da8 !important;
  font-size: 0.82rem !important;
  font-weight: 750;
  margin: 0 !important;
  padding: 0.4rem 0.7rem;
}

.future-ledger {
  background: #fffdf7;
  border-top: 1px solid #f2e5bf;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(150px, 1fr));
  gap: 1rem;
}

.metric-card {
  background: #f3f6f9;
  border-radius: 0.6rem;
  padding: 1.25rem;
}

.metric-card span {
  display: block;
  color: #7e8299;
  font-size: 0.9rem;
  margin-bottom: 0.4rem;
}

.metric-card strong {
  color: #3f4254;
  font-size: 1.25rem;
}

.metric-card.featured {
  background: color-mix(in srgb, var(--primary-color) 12%, #fff);
}

.metric-card.featured strong {
  color: var(--primary-color);
}

@media (max-width: 991px) {
  .plan-navigation {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .commission-toolbar {
    align-items: stretch;
    margin-left: 0 !important;
    width: 100%;
  }

  .plan-version-select {
    min-width: 220px;
    max-width: none;
    width: 100%;
  }

  .metrics-grid {
    grid-template-columns: repeat(2, minmax(140px, 1fr));
  }

  .dashboard-card-header,
  .settlement-actions,
  .settlement-detail-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .settlement-overview-grid {
    grid-template-columns: repeat(2, minmax(140px, 1fr));
  }

  .seller-settlement-grid {
    grid-template-columns: 1fr;
  }

  .seller-commission-panel,
  .seller-finance-panel {
    border-left: 0;
    border-top: 1px solid #edf0f5;
  }

  .seller-finance-panel {
    text-align: left;
  }

  .timing-setting {
    grid-template-columns: 1fr;
  }

  .commission-table-header {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .commission-table-identity {
    grid-template-columns: 1fr;
    width: 100%;
  }

  .commission-table-usage {
    margin-left: auto;
  }

  .commission-tier-row {
    grid-template-columns:
      32px repeat(4, minmax(130px, 1fr)) minmax(210px, 1.4fr)
      38px;
    overflow-x: auto;
  }

  .tier-result {
    min-width: 210px;
  }
}

@media (max-width: 575px) {
  .metrics-grid {
    grid-template-columns: 1fr;
  }

  .settlement-overview-grid {
    grid-template-columns: 1fr;
  }

  .future-commission-banner,
  .seller-settlement-header {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .future-commission-badge,
  .seller-settlement-tags {
    margin-left: 0;
  }

  .payment-item {
    grid-template-columns: 1fr;
  }

  .competence-card {
    flex-direction: column;
  }

  .timing-options {
    grid-template-columns: 1fr;
  }

  .commission-tier-heading {
    align-items: flex-start;
    flex-direction: column;
    gap: 0.75rem;
  }

  .commission-tier-actions {
    align-items: stretch;
    flex-direction: column;
    width: 100%;
  }

  .commission-tier-row {
    grid-template-columns: 1fr;
  }

  .tier-sequence,
  .tier-result,
  .commission-tier-row > .btn {
    margin-top: 0;
  }

  .plan-navigation-summary {
    margin-left: auto;
  }

  .history-grouping {
    width: 100%;
  }

  .overview-kpi strong {
    white-space: normal;
  }

  .ranking-table td,
  .ranking-table th {
    padding-left: 1rem;
    padding-right: 1rem;
  }
}
</style>
